// --- Imports ---
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSilentSaveListener } from '../../../hooks/useSilentSaveListener';
import { useSafeSave } from '../../../hooks/useSafeSave';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { MANUAL_CONTROLS_DATA } from '../../../constants/manualControlsData';
import { motion, AnimatePresence } from 'framer-motion';
// Ensure all used icons are imported
import { Save, CheckSquare, Info, ShieldCheck, X, Edit3, PlusCircle, RefreshCw, BrainCircuit, FileText, ChevronDown, Edit, Trash2, ArrowLeft } from 'lucide-react';
import Button from '../../common/Button';
import AiAssistantButton from '../../common/AiAssistantButton';
import { useAuth } from '../../../context/AuthContext';
import WorkshopExportMenu from '../../common/WorkshopExportMenu';
import { useBroadcast } from '../../../hooks/useBroadcast';
import { useAnalysis } from '../../../context/AnalysisContext';
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier1Store from '../../../stores/useAtelier1Store';
import securityControlsService from '../../../services/securityControlsService';
import { handleAIError } from '../../../utils/aiErrorHandler';
import { normalizeRuleStatus, getRuleStatusConfig } from '../../../utils/ruleStatusNormalizer';
import { showSuccessToast, showErrorToast, showLoadingToast, updateToast, showInfoToast } from '../../../utils/toastUtils';
import SecurityControlsPdfGenerator from '../../../utils/securityControlsPdfGenerator';
import GlobalAiSuggestionModal from './GlobalAiSuggestionModal';
import ManualControlModal from './ManualControlModal';
import DeleteConfirmationModal from '../../common/DeleteConfirmationModal';

// --- Constants and Helpers ---
const getSeverityLevelInfo = (severity, t) => {
    const lowerSeverity = severity?.toLowerCase() || '';
    switch (lowerSeverity) {
        case 'catastrophic':
            return { level: 'G5', label: t('securityControls.severity.catastrophic'), textClass: 'text-white', bgClass: 'bg-black' };
        case 'critical':
            return { level: 'G4', label: t('securityControls.severity.critical'), textClass: 'text-white', bgClass: 'bg-red-600' };
        case 'major': case 'majeure':
            return { level: 'G3', label: t('securityControls.severity.major'), textClass: 'text-black', bgClass: 'bg-yellow-500' };
        case 'moderate': case 'significative':
            return { level: 'G2', label: t('securityControls.severity.moderate'), textClass: 'text-black', bgClass: 'bg-yellow-200' };
        case 'minor': case 'mineure':
            return { level: 'G1', label: t('securityControls.severity.minor'), textClass: 'text-black', bgClass: 'bg-cyan-300' };
        default:
            // console.warn(`Unknown severity: '${severity}'`);
            return { level: 'N/A', label: t('securityControls.severity.undefined'), textClass: 'text-gray-800', bgClass: 'bg-gray-200' };
    }
};
// Helper functions for translated constants
const getDecisionOptions = (t) => [
    { value: 'implemented', label: t('securityControls.decision.implemented') },
    { value: 'partial', label: t('securityControls.decision.partial') },
    { value: 'planned', label: t('securityControls.decision.planned') },
    { value: 'apply_before', label: t('securityControls.decision.apply_before') },
    { value: 'abandoned', label: t('securityControls.decision.abandoned') },
];

const getTreatmentOptions = (t) => [
    { value: 'reduce', label: t('securityControls.treatment.reduce') },
    { value: 'transfer', label: t('securityControls.treatment.transfer') },
    { value: 'avoid', label: t('securityControls.treatment.avoid') },
    { value: 'accept', label: t('securityControls.treatment.accept') },
];

// Function to get badge color and style for measure types
const getMeasureTypeBadge = (measureType) => {
    if (!measureType) return null;

    const typeConfig = {
        'gouvernance et anticipation': {
            color: 'bg-blue-100 text-blue-800 border-blue-200',
            label: 'Gouvernance'
        },
        'protection': {
            color: 'bg-green-100 text-green-800 border-green-200',
            label: 'Protection'
        },
        'défense': {
            color: 'bg-orange-100 text-orange-800 border-orange-200',
            label: 'Défense'
        },
        'résilience': {
            color: 'bg-purple-100 text-purple-800 border-purple-200',
            label: 'Résilience'
        }
    };

    return typeConfig[measureType] || null;
};

const getSeverityOptions = (t) => [
    { value: 'minor', label: `${t('securityControls.severity.minor')} (G1)` },
    { value: 'moderate', label: `${t('securityControls.severity.moderate')} (G2)` },
    { value: 'major', label: `${t('securityControls.severity.major')} (G3)` },
    { value: 'critical', label: `${t('securityControls.severity.critical')} (G4)` },
    { value: 'catastrophic', label: `${t('securityControls.severity.catastrophic')} (G5)` },
];

const SEVERITY_LEVEL_MAP = { 'minor': 1, 'moderate': 2, 'major': 3, 'critical': 4, 'catastrophic': 5, };
const WORKSHOP_MEASURE_NAVIGATION_KEY = 'ebiosrm_measure_navigation_target';
const WORKSHOP_MEASURE_RETURN_KEY = 'ebiosrm_measure_return_context';
const WORKSHOP5_RESTORE_CONTEXT_KEY = 'ebiosrm_workshop5_restore_context';

const normalizeMeasureMatchValue = (value) =>
    (value || '').toString().trim().toLowerCase();

const flashHighlightElement = (element) => {
    if (!element || typeof window === 'undefined') return;

    const previousTransition = element.style.transition;
    const previousBackgroundColor = element.style.backgroundColor;
    const previousBoxShadow = element.style.boxShadow;

    element.style.transition = 'background-color 0.25s ease, box-shadow 0.25s ease';
    element.style.backgroundColor = '#FEF3C7';
    element.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.75)';

    window.setTimeout(() => {
        element.style.backgroundColor = previousBackgroundColor;
        element.style.boxShadow = previousBoxShadow;
        element.style.transition = previousTransition;
    }, 1800);
};

// --- Modals ---
// Updated ControlSelectionModal to handle loading/error states and use fetched controls
const ControlSelectionModal = ({ isOpen, onClose, modalAvailableControls, initialSelectedIds, onSave, isLoading, error }) => {
    const { t } = useTranslation();
    const [currentSelection, setCurrentSelection] = useState(new Set(initialSelectedIds || []));
    const [filterInfo, setFilterInfo] = useState(null);

    useEffect(() => {
        // Reset selection when initialSelectedIds change (modal reopens for different context)
        setCurrentSelection(new Set(initialSelectedIds || []));
    }, [initialSelectedIds]);

    // Extract filter information from the context when controls are loaded
    useEffect(() => {
        if (!isLoading && modalAvailableControls && modalAvailableControls.length > 0) {
            // Get unique pillars and treatment strategies from the available controls
            const pillars = [...new Set(modalAvailableControls.map(c => c.pillar).filter(Boolean))];
            const strategies = [...new Set(modalAvailableControls.map(c => c.risk_treatment_strategy).filter(Boolean))];

            setFilterInfo({ pillars, strategies });
        }
    }, [isLoading, modalAvailableControls]);

    const handleToggle = (controlId) => {
        setCurrentSelection(prev => {
            const ns = new Set(prev);
            if (ns.has(controlId)) {
                ns.delete(controlId);
            } else {
                ns.add(controlId);
            }
            return ns;
        });
    };

    const handleSave = () => {
        onSave(Array.from(currentSelection));
        onClose(); // Close modal after saving
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4 print:hidden">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg relative"> {/* Increased max-width */}
                <button onClick={onClose} className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"><X size={20} /></button>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('securityControls.modal.addControls')}</h3>

                {/* Filter information display */}
                {filterInfo && (
                    <div className="mb-4 text-xs bg-blue-50 p-2 rounded border border-blue-100">
                        <p className="font-medium text-blue-700 mb-1">{t('securityControls.status.controlsFiltered')}</p>
                        {filterInfo.pillars.length > 0 && (
                            <p className="text-blue-600">
                                <span className="font-medium">{t('securityControls.status.pillarOfEvent')}</span> {filterInfo.pillars[0]}
                            </p>
                        )}
                        {filterInfo.strategies.length > 0 && (
                            <p className="text-blue-600">
                                <span className="font-medium">{t('securityControls.status.treatmentStrategy')}</span> {filterInfo.strategies[0]}
                            </p>
                        )}
                    </div>
                )}

                <div className="space-y-3 max-h-80 overflow-y-auto pr-2 mb-6 border border-gray-200 rounded p-3 bg-gray-50 min-h-[100px]"> {/* Increased max-height */}
                    {isLoading && (
                        <div className="flex items-center justify-center h-full text-gray-500">
                            <RefreshCw size={18} className="animate-spin mr-2" />
                            <span>{t('securityControls.status.loadingControls')}</span>
                        </div>
                    )}
                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-center">
                            <X size={16} className="text-red-500 mr-2 flex-shrink-0" />
                            <span>{t('securityControls.modal.error')} {error}</span>
                        </div>
                    )}
                    {!isLoading && !error && modalAvailableControls && modalAvailableControls.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4 italic">{t('securityControls.status.noControlsFound')}</p>
                    )}
                    {!isLoading && !error && modalAvailableControls && modalAvailableControls.length > 0 && (
                        modalAvailableControls.map(control => (
                            <div key={control.id || control._id} className="flex items-center p-2 bg-white rounded border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors">
                                <input
                                    type="checkbox"
                                    id={`modal-ctrl-${control.id || control._id}`}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3 shrink-0"
                                    checked={currentSelection.has(control.id || control._id)}
                                    onChange={() => handleToggle(control.id || control._id)}
                                />
                                <label htmlFor={`modal-ctrl-${control.id || control._id}`} className="text-sm text-gray-700 cursor-pointer flex flex-col">
                                    <div className="flex items-center space-x-2 mb-1">
                                        <span className="font-medium">{control.measure_description || control.name}</span>
                                        {control.measure_type && getMeasureTypeBadge(control.measure_type) && (
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getMeasureTypeBadge(control.measure_type).color}`}>
                                                {getMeasureTypeBadge(control.measure_type).label}
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-xs text-gray-500">{t('securityControls.labels.pillar')}: {control.pillar || t('securityControls.labels.notSpecified')} | {t('securityControls.labels.treatment')}: {control.risk_treatment_strategy || t('securityControls.labels.notSpecified')}</span>
                                </label>
                            </div>
                        ))
                    )}
                </div>

                <div className="flex justify-end space-x-3">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-sm">{t('securityControls.buttons.cancel')}</button>
                    <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">{t('securityControls.buttons.addSelection')}</button>
                </div>
            </div>
        </div>,
        document.body
    );
};

const AiSuggestionModal = ({ isOpen, onClose, context, onGenerate, suggestions, isLoading, error, onSelectSuggestion, selectedSuggestions, onAddSelected }) => {
    const { t } = useTranslation();
    if (!isOpen || !context) return null;
    const { rule, de } = context;
    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[110] p-4 print:hidden">
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg relative">
                <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close modal">
                    <X size={22} />
                </button>
                <h3 className="text-xl font-semibold text-gray-800 mb-1">{t('securityControls.modal.aiSuggestions')}</h3>
                <p className="text-sm text-gray-500 mb-4">
                    {t('securityControls.modal.forRule')} <span className="font-medium text-gray-700">{rule?.name}</span><br />
                    {t('securityControls.modal.linkedToEvent')} <span className="font-medium text-gray-700">{de?.name}</span> ({t('securityControls.sections.severity')}: {getSeverityLevelInfo(de?.severity, t).label})
                </p>
                <div className="mb-5">
                    <div className="flex gap-2">
                        {suggestions.length > 0 && (
                            <button
                                onClick={onGenerate}
                                disabled={isLoading}
                                className="flex-1 flex items-center justify-center px-4 py-2 bg-white text-indigo-600 border border-indigo-200 rounded-md hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium shadow-sm"
                            >
                                {isLoading ? (
                                    <><RefreshCw size={16} className="animate-spin mr-2" /> {t('securityControls.status.generating')}</>
                                ) : (
                                    <><RefreshCw size={16} className="mr-1.5" /> {t('securityControls.buttons.regenerate', 'Actualiser / Autres')}</>
                                )}
                            </button>
                        )}
                        <button
                            onClick={onGenerate}
                            disabled={isLoading || suggestions.length > 0}
                            className={`flex-1 flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium shadow-sm ${suggestions.length > 0 ? 'hidden' : ''}`}
                        >
                            {isLoading ? (
                                <><RefreshCw size={16} className="animate-spin mr-2" /> {t('securityControls.status.generating')}</>
                            ) : (
                                <><BrainCircuit size={16} className="mr-1.5" /> {t('securityControls.modal.generateSuggestions')}</>
                            )}
                        </button>
                    </div>
                    {error && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-center">
                            <X size={16} className="text-red-500 mr-2 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>
                <div className="mb-5 min-h-[150px] max-h-[300px] overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50 shadow-inner">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">{t('securityControls.status.suggestionsGenerated')}</h4>
                    {isLoading && !suggestions.length && (
                        <div className="flex items-center justify-center h-full text-gray-500"><RefreshCw size={18} className="animate-spin mr-2" /><span>{t('securityControls.buttons.loading')}</span></div>
                    )}
                    {!isLoading && !suggestions.length && !error && (
                        <p className="text-sm text-gray-400 text-center py-4 italic">{t('securityControls.status.noSuggestions')}</p>
                    )}
                    {!isLoading && suggestions.length > 0 && (
                        <div className="space-y-2">
                            {suggestions.map((suggestion, index) => {
                                // Handle both old format (strings) and new format (objects)
                                const suggestionName = typeof suggestion === 'string' ? suggestion : suggestion.name;
                                const measureType = typeof suggestion === 'object' ? suggestion.measure_type : null;
                                const isSelected = selectedSuggestions.some(selected => {
                                    const selectedKey = typeof selected === 'string' ? selected : selected.name;
                                    return selectedKey === suggestionName;
                                });

                                return (
                                    <label key={index} htmlFor={`ai-ctrl-${index}`} className="flex items-start p-2.5 bg-white rounded border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors cursor-pointer shadow-sm">
                                        <input
                                            type="checkbox"
                                            id={`ai-ctrl-${index}`}
                                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-3 shrink-0 mt-0.5"
                                            checked={isSelected}
                                            onChange={() => onSelectSuggestion(suggestion)}
                                        />
                                        <div className="flex-1">
                                            <div className="flex flex-col space-y-1">
                                                <span className="text-sm text-gray-800">{suggestionName}</span>
                                                {measureType && getMeasureTypeBadge(measureType) && (
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border w-fit ${getMeasureTypeBadge(measureType).color}`}>
                                                        {getMeasureTypeBadge(measureType).label}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="flex justify-end space-x-3 pt-3 border-t border-gray-200">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-sm">{t('securityControls.buttons.cancel')}</button>
                    <button onClick={onAddSelected} disabled={selectedSuggestions.length === 0} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm disabled:opacity-50">
                        {t('securityControls.buttons.addSelection')}
                    </button>
                </div>
            </motion.div>
        </div>,
        document.body
    );
};


// --- Main Component ---
const SecurityControls = () => {
    const { t } = useTranslation();
    const atelier1Store = useAtelier1Store();
    const analysisStore = useAnalysisStore();
    const { selectAnalysis } = useAnalysis();
    const broadcastUpdate = useBroadcast();
    const { companyAiEnabled } = useAuth();

    const currentAnalysisControlPlan = atelier1Store.controlPlan;
    const allControls = atelier1Store.allControls;
    const currentSecurityFramework = atelier1Store.securityFramework;
    const currentBusinessValues = atelier1Store.businessValues;
    const currentDreadedEvents = atelier1Store.dreadedEvents;
    const ruleGapComments = currentSecurityFramework?.ruleGapComments || {};
    const currentAnalysis = analysisStore.currentAnalysis;
    const currentContextData = atelier1Store.contextData;

    // Safe save hook - prevents auto-save from overwriting data when not fully loaded
    const { markDataLoaded, markModified, canAutoSave, clearModified, useFormChangeDetector } = useSafeSave('SecurityControls');
    const formChangeRef = useFormChangeDetector();

    // Removed sensitive data logging for security

    // Ref to prevent useEffect reset immediately after save
    const justSaved = useRef(false);

    // Local state for UI interactions
    const [selectedControlsForRuleDE, setSelectedControlsForRuleDE] = useState({}); // { [ruleId_deId]: [controlObject1, controlObject2,...] } <- Store objects
    const [residualSeverities, setResidualSeverities] = useState({}); // { [deId]: 'severity' }
    const [responsiblePersons, setResponsiblePersons] = useState({}); // { [ruleId_deId]: { [controlId]: 'name' } }
    const [implementationDecisions, setImplementationDecisions] = useState({}); // { [ruleId_deId]: { [controlId]: { decision: 'value', date: 'YYYY-MM-DD' } } }
    const [treatmentOptions, setTreatmentOptions] = useState({}); // { [ruleId_deId]: 'treatmentValue' }
    const [treatmentJustifications, setTreatmentJustifications] = useState({}); // { [ruleId_deId]: 'justificationText' }
    const [returnToWorkshop5, setReturnToWorkshop5] = useState(null);

    // Non-intentional risk likelihood state
    const [vraisemblanceInitiale, setVraisemblanceInitiale] = useState({});
    const [vraisemblanceResiduelle, setVraisemblanceResiduelle] = useState({});

    // Guide state
    const [isGuideOpen, setIsGuideOpen] = useState(false);
    const [isPdfMenuOpen, setIsPdfMenuOpen] = useState(false);
    const [isDocxMenuOpen, setIsDocxMenuOpen] = useState(false);

    const [isControlModalOpen, setIsControlModalOpen] = useState(false);
    const [modalContext, setModalContext] = useState(null); // { ruleId, deId }

    // State for Control Selection Modal Data
    const [modalAvailableControls, setModalAvailableControls] = useState([]);
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [modalError, setModalError] = useState(null);


    // State for AI Suggestions Modal
    const [isAiSuggestionModalOpen, setIsAiSuggestionModalOpen] = useState(false);
    const [aiModalContext, setAiModalContext] = useState(null); // { rule, de }
    const [aiSuggestionsList, setAiSuggestionsList] = useState([]); // Array of suggestion strings
    const [aiSuggestionsCache, setAiSuggestionsCache] = useState({}); // { [key]: [suggestions] }
    const [isLoadingAiSuggestions, setIsLoadingAiSuggestions] = useState(false);
    const [aiSuggestionError, setAiSuggestionError] = useState(null);
    const [selectedAiSuggestionNames, setSelectedAiSuggestionNames] = useState([]); // Array of selected suggestion strings

    // State for Global AI Generation
    const [isGlobalAiModalOpen, setIsGlobalAiModalOpen] = useState(false);
    const [isGlobalAiLoading, setIsGlobalAiLoading] = useState(false);
    const [globalAiSuggestions, setGlobalAiSuggestions] = useState({}); // { [ruleId_deId]: [suggestions] }
    const [globalAiCache, setGlobalAiCache] = useState({}); // { [ruleId_deId]: [suggestions] }
    const [globalAiError, setGlobalAiError] = useState(null);
    const [isLoadingSave, setIsLoadingSave] = useState(false);

    // Manual Control Modal State
    const [isManualModalOpen, setIsManualModalOpen] = useState(false);
    const [manualModalContext, setManualModalContext] = useState(null); // { ruleId, deId }

    // Delete Confirmation State
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        isOpen: false,
        key: null,
        controlId: null,
        itemName: ''
    });

    const openManualControlModal = (ruleId, deId, control = null) => {
        setManualModalContext({ ruleId, deId, control });
        setIsManualModalOpen(true);
    };

    const handleDeleteControl = (key, controlId, controlName = 'ce contrôle') => {
        setDeleteConfirmation({
            isOpen: true,
            key,
            controlId,
            itemName: controlName
        });
    };

    const confirmDeleteControl = () => {
        const { key, controlId } = deleteConfirmation;
        if (!key || !controlId) return;

        setSelectedControlsForRuleDE(prev => {
            const currentList = prev[key] || [];
            return {
                ...prev,
                [key]: currentList.filter(c => c.id !== controlId)
            };
        });

        // Clean up responsible persons and decisions
        setResponsiblePersons(prev => {
            if (!prev[key]) return prev;
            const updated = { ...prev[key] };
            delete updated[controlId];
            return { ...prev, [key]: updated };
        });

        setImplementationDecisions(prev => {
            if (!prev[key]) return prev;
            const updated = { ...prev[key] };
            delete updated[controlId];
            return { ...prev, [key]: updated };
        });

        markModified();
        setDeleteConfirmation({ isOpen: false, key: null, controlId: null, itemName: '' });
    };

    const handleEditControl = (key, control) => {
        // key = "ruleId_deId" — deId may be "__no_de__" for orphans
        const lastUnderscore = key.lastIndexOf('_');
        const ruleId = key.substring(0, lastUnderscore);
        const deIdPart = key.substring(lastUnderscore + 1);
        const deId = deIdPart === '__no_de__' ? null : deIdPart;
        openManualControlModal(ruleId, deId, control);
    };

    const handleReturnToWorkshop5 = () => {
        if (typeof window === 'undefined') {
            return;
        }

        const restorePayload = {
            ...returnToWorkshop5,
            sourceTab: returnToWorkshop5?.sourceTab || 'atelier5-synthese-scenarios',
            viewMode: returnToWorkshop5?.viewMode || 'carousel',
            cardTab: returnToWorkshop5?.cardTab || 'measures',
            workshopViewMode: returnToWorkshop5?.workshopViewMode || 'detailed'
        };

        localStorage.removeItem(WORKSHOP_MEASURE_NAVIGATION_KEY);
        localStorage.removeItem(WORKSHOP_MEASURE_RETURN_KEY);
        localStorage.setItem(WORKSHOP5_RESTORE_CONTEXT_KEY, JSON.stringify(restorePayload));
        localStorage.setItem('ebiosrm_switch_to_tab', restorePayload.sourceTab);
        window.dispatchEvent(new CustomEvent('ebiosrm_switch_tab'));
        setReturnToWorkshop5(null);
    };

    const isReturnTargetControl = (control) => {
        if (!returnToWorkshop5 || !control) {
            return false;
        }

        const targetId = String(returnToWorkshop5.controlId || returnToWorkshop5.measureId || '');
        const controlId = String(control.id || '');
        const targetName = normalizeMeasureMatchValue(returnToWorkshop5.measureName || returnToWorkshop5.normalizedMeasureName || '');
        const controlName = normalizeMeasureMatchValue(control.name || '');

        return (targetId && controlId === targetId) || (targetName && controlName === targetName);
    };

    const handleSaveManualControl = ({ name, description, measureType, id }) => {
        if (!manualModalContext) return;
        const { ruleId, deId, control: editingControl } = manualModalContext;
        const deIdPart = deId != null && deId !== '' ? deId : '__no_de__';
        const key = `${ruleId}_${deIdPart}`;

        setSelectedControlsForRuleDE(prev => {
            const currentList = prev[key] || [];

            if (editingControl) {
                // Update existing control
                return {
                    ...prev,
                    [key]: currentList.map(c => c.id === editingControl.id ? {
                        ...c,
                        name,
                        description,
                        measure_type: measureType
                    } : c)
                };
            } else {
                // Add new control
                const newControl = {
                    id: `manual-${Date.now()}`,
                    name,
                    description,
                    type: 'Manual',
                    isNewSuggestion: false,
                    measure_type: measureType || 'protection'
                };
                return {
                    ...prev,
                    [key]: [...currentList, newControl]
                };
            }
        });

        markModified();
        showSuccessToast(editingControl ? 'Contrôle modifié' : 'Contrôle manuel ajouté');
    };



    // --- Data Derivation --- (useMemo to optimize)
    // REMOVED: const rules = useMemo(() => {
    //     // Get rules from the 'definitions' property if framework exists
    //     // return currentSecurityFramework?.definitions || [];
    // }, [currentSecurityFramework]);

    // ---> NEW: Calculate linked rule/event pairs based on SecurityFramework links <---
    console.log('SecurityControls: Using data from AnalysisContext:', {
        currentSecurityFramework,
        currentBusinessValues,
        currentDreadedEvents
    });

    // Extract plan data from securityControls and AnalysisContext
    // FIXED: Always show ALL rule-dreaded event pairs from security framework,
    // even if they don't have controls yet. This prevents pairs from disappearing
    // when they have no controls assigned.
    const linkedPairs = useMemo(() => {
        const pairs = [];
        const pairKeys = new Set(); // Track unique pairs to avoid duplicates

        // Always start with the security framework links as the source of truth
        const selectedRuleMap = new Map(); // Map rule.id (string) to rule object
        const ruleLinks = currentSecurityFramework?.ruleDreadedEvents || {}; // { ruleId: [deId1, deId2], ... }
        const allEventsMap = new Map((currentDreadedEvents || []).map(de => [String(de.id), de])); // Map de.id (string) to de object

        // Populate selectedRuleMap from selectedRules (which is grouped by frameworkId)
        if (currentSecurityFramework?.selectedRules) {
            Object.values(currentSecurityFramework.selectedRules).forEach(rulesInFramework => {
                if (Array.isArray(rulesInFramework)) {
                    rulesInFramework.forEach(rule => {
                        const ruleId = rule?.id;
                        if (rule && ruleId) {
                            selectedRuleMap.set(String(ruleId), rule);
                        }
                    });
                }
            });
        }

        // Create pairs from security framework links (this ensures ALL linked pairs are shown)
        for (const ruleIdStr in ruleLinks) {
            const linkedDeIds = ruleLinks[ruleIdStr];
            const rule = selectedRuleMap.get(ruleIdStr);

            if (rule && Array.isArray(linkedDeIds) && linkedDeIds.length > 0) {
                linkedDeIds.forEach(deId => {
                    if (deId === '__v') return; // ponytail: skip placeholder sentinel
                    let de = allEventsMap.get(String(deId));
                    // ponytail: virtual ER not in allEventsMap, create dummy so pairs still form
                    if (!de) {
                        de = { id: deId, name: '' };
                    }
                    const pairKey = `${rule.id}_${de.id}`;
                    if (!pairKeys.has(pairKey)) {
                        pairs.push({ rule, de });
                        pairKeys.add(pairKey);
                    }
                });
            }
        }

        // Also check plan data for any additional pairs that might not be in framework links
        const planObject = currentAnalysisControlPlan?.planData?.plan;
        if (planObject && typeof planObject === 'object' && Object.keys(planObject).length > 0) {
            console.log("SC: Checking plan data for additional pairs");

            Object.keys(planObject).forEach(key => {
                const [ruleId, deIdStr] = key.split('_');
                if (ruleId && deIdStr && deIdStr !== '__v' && !pairKeys.has(key)) {
                    const deId = Number(deIdStr);
                    console.log(`SC: Found additional pair in plan: Rule ${ruleId} - Event ${deId}`);

                    let rule = selectedRuleMap.get(ruleId);
                    if (!rule) {
                        // stale plan entry for deleted rule, skip instead of creating dummy
                        return;
                    }
                    let de = allEventsMap.get(String(deId));
                    // keep dummy de handling as before if de missing but rule exists
                    if (!de) {
                        de = { id: deId, name: `Événement ${deId}` };
                    }

                    pairs.push({ rule, de });
                    pairKeys.add(key);
                }
            });
        }

        console.log("SC: Created pairs from security framework and plan data:", pairs);

        // console.log("SC: Calculated linkedPairs:", pairs); // Debug log
        // ---> Sort by Rule Name/ID (natural order: 5.7, 5.8, 5.10) <---
        pairs.sort((a, b) => {
            const ruleNameA = a.rule?.name?.toLowerCase() || String(a.rule?.id);
            const ruleNameB = b.rule?.name?.toLowerCase() || String(b.rule?.id);
            const cmp = ruleNameA.localeCompare(ruleNameB, undefined, { numeric: true });
            if (cmp !== 0) return cmp;
            const deNameA = a.de?.name?.toLowerCase() || '';
            const deNameB = b.de?.name?.toLowerCase() || '';
            return deNameA.localeCompare(deNameB);
        });

        console.log("SC: Sorted linkedPairs:", pairs); // Debug log after sorting

        return pairs;

    }, [currentAnalysisControlPlan, currentSecurityFramework?.selectedRules, currentSecurityFramework?.ruleDreadedEvents, currentDreadedEvents]); // Dependencies

    const erPairs = useMemo(() => {
        const isNonIntPair = (p) => {
            if (p.de?.businessValue === '__global__') return true;
            if (p.de?.id != null) {
                const fullDe = (currentDreadedEvents || []).find(d => String(d.id) === String(p.de.id));
                return fullDe?.businessValue === '__global__';
            }
            return false;
        };
        const pairs = linkedPairs.filter(p => !isNonIntPair(p));

        // Add orphan rules (no linked DE at all in ruleDreadedEvents) with a sentinel DE so they
        // appear in the table. Rules that have ANY link (even to non-intentional DEs) are skipped —
        // they show up in Table 2 or have been filtered out above, and should NOT be duplicated here.
        const pairedRuleIds = new Set(pairs.map(p => String(p.rule?.id)));
        const ruleLinks = currentSecurityFramework?.ruleDreadedEvents || {};
        if (currentSecurityFramework?.selectedRules) {
            Object.values(currentSecurityFramework.selectedRules).forEach(rulesInFramework => {
                if (Array.isArray(rulesInFramework)) {
                    rulesInFramework.forEach(rule => {
                        const ruleId = rule?.id && String(rule.id);
                        if (ruleId && !pairedRuleIds.has(ruleId)) {
                            const linkedDeIds = ruleLinks[ruleId];
                            if (!linkedDeIds || linkedDeIds.length === 0 || (linkedDeIds.length === 1 && linkedDeIds[0] === '__v')) {
                                pairs.push({
                                    rule,
                                    de: { id: null, name: null, severity: null, businessValue: '' }
                                });
                            }
                        }
                    });
                }
            });
        }

        // Sort orphans into the same natural order as the rest
        pairs.sort((a, b) => {
            const ruleNameA = a.rule?.name?.toLowerCase() || String(a.rule?.id);
            const ruleNameB = b.rule?.name?.toLowerCase() || String(b.rule?.id);
            const cmp = ruleNameA.localeCompare(ruleNameB, undefined, { numeric: true });
            if (cmp !== 0) return cmp;
            const deNameA = a.de?.name?.toLowerCase() || '';
            const deNameB = b.de?.name?.toLowerCase() || '';
            return deNameA.localeCompare(deNameB);
        });

        return pairs;
    }, [linkedPairs, currentSecurityFramework, currentDreadedEvents]);

    const nonIntentionalPairs = useMemo(() =>
        linkedPairs.filter(p => {
            if (p.de?.businessValue === '__global__') return true;
            if (p.de?.id != null) {
                const fullDe = (currentDreadedEvents || []).find(d => String(d.id) === String(p.de.id));
                if (fullDe?.businessValue === '__global__') return true;
            }
            return false;
        }),
        [linkedPairs, currentDreadedEvents]
    );

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const rawContext = localStorage.getItem(WORKSHOP_MEASURE_RETURN_KEY);
        if (!rawContext) {
            return;
        }

        try {
            const parsedContext = JSON.parse(rawContext);
            if (parsedContext?.sourceTab === 'atelier5-synthese-scenarios' && parsedContext?.targetTab === 'security-controls') {
                setReturnToWorkshop5(parsedContext);
            }
        } catch (error) {
            console.warn('SC: Failed to parse return-to-Workshop-5 context:', error);
        } finally {
            localStorage.removeItem(WORKSHOP_MEASURE_RETURN_KEY);
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || Object.keys(selectedControlsForRuleDE).length === 0) {
            return;
        }

        const rawTarget = localStorage.getItem(WORKSHOP_MEASURE_NAVIGATION_KEY);
        if (!rawTarget) {
            return;
        }

        let target;
        try {
            target = JSON.parse(rawTarget);
        } catch (error) {
            console.warn('SC: Failed to parse pending measure highlight payload:', error);
            localStorage.removeItem(WORKSHOP_MEASURE_NAVIGATION_KEY);
            return;
        }

        if (target?.tab !== 'security-controls' || target?.sourceType !== 'atelier1') {
            return;
        }

        const targetIds = [target.controlId, target.measureId]
            .filter(value => value !== undefined && value !== null && value !== '')
            .map(value => String(value));
        const targetName = normalizeMeasureMatchValue(target.measureName || target.normalizedMeasureName);

        const hasMatch = Object.values(selectedControlsForRuleDE).some((controls) =>
            (controls || []).some((control) => {
                const controlIds = [control?.id, control?.controlId]
                    .filter(value => value !== undefined && value !== null && value !== '')
                    .map(value => String(value));
                const controlName = normalizeMeasureMatchValue(control?.name || control?.measure_description);

                return controlIds.some(id => targetIds.includes(id)) || (targetName && controlName === targetName);
            })
        );

        if (!hasMatch) {
            return;
        }

        const timer = window.setTimeout(() => {
            const anchorElement = Array.from(document.querySelectorAll('[data-control-anchor="true"]')).find((element) => {
                const elementId = element.getAttribute('data-control-id');
                const elementName = normalizeMeasureMatchValue(element.getAttribute('data-control-name'));

                return (
                    (targetIds.length > 0 && elementId && targetIds.includes(String(elementId))) ||
                    (targetName && elementName === targetName)
                );
            });

            if (!anchorElement) {
                return;
            }

            anchorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            flashHighlightElement(anchorElement);
            localStorage.removeItem(WORKSHOP_MEASURE_NAVIGATION_KEY);
        }, 180);

        return () => window.clearTimeout(timer);
    }, [linkedPairs.length, selectedControlsForRuleDE]);


    // --- Effect to initialize local state from loaded plan ---
    useEffect(() => {
        // Only run the reset logic if we didn't just save from this component
        if (justSaved.current) {
            console.log("SC Effect: Skipping state reset because save was just initiated.");
            // Reset the flag *after* skipping, so subsequent loads work
            // justSaved.current = false; // Let the finally block in save handle this
            return;
        }

        console.log("SC Effect: Running with received plan object:", currentAnalysisControlPlan);
        // ---> ADD Log for the actual plan object being processed <---
        const planObject = currentAnalysisControlPlan?.planData?.plan;
        console.log("SC Effect: Extracted planData.plan:", planObject);

        // Load AI suggestions cache from database
        const cachedAiSuggestions = currentAnalysisControlPlan?.aiSuggestionsCache;
        if (cachedAiSuggestions && typeof cachedAiSuggestions === 'object') {
            console.log("✅ Loading AI suggestions cache from database:", cachedAiSuggestions);
            setGlobalAiCache(cachedAiSuggestions);
        }

        // Ensure planObject exists, is an object, and allControls are loaded
        if (planObject && typeof planObject === 'object' && allControls) {

            // Use the nested plan directly
            const plan = planObject;

            const newSelectedControls = {};
            const newResidualSeverities = {};
            const newResponsiblePersons = {};
            const newImplementationDecisions = {};
            const newTreatmentOptions = {};
            const newTreatmentJustifications = {};
            const newVraisemblanceInitiale = {};
            const newVraisemblanceResiduelle = {};

            Object.entries(plan).forEach(([key, entry]) => {
                // Treatment Option
                if (entry.treatmentOption !== undefined && entry.treatmentOption !== null) {
                    newTreatmentOptions[key] = entry.treatmentOption;
                }
                // Justification
                if (entry.treatmentJustification !== undefined && entry.treatmentJustification !== null) {
                    newTreatmentJustifications[key] = entry.treatmentJustification;
                }
                // Residual Severity
                if (entry.residualSeverity !== undefined && entry.residualSeverity !== null) {
                    newResidualSeverities[key] = entry.residualSeverity;
                }
                // Vraisemblance (non-intentional risk)
                if (entry.vraisemblanceInitiale !== undefined && entry.vraisemblanceInitiale !== null) {
                    newVraisemblanceInitiale[key] = entry.vraisemblanceInitiale;
                }
                if (entry.vraisemblanceResiduelle !== undefined && entry.vraisemblanceResiduelle !== null) {
                    newVraisemblanceResiduelle[key] = entry.vraisemblanceResiduelle;
                }

                // Controls, Responsible, Decision
                if (entry.controls && entry.controls.length > 0) {
                    // Map saved controls to full control objects
                    newSelectedControls[key] = entry.controls.map(savedControl => {
                        // Basic check for controlId existence before proceeding
                        if (!savedControl.controlId) {
                            console.warn("Saved control entry missing controlId:", savedControl, "for key:", key);
                            return null; // Skip this invalid entry
                        }
                        const fullControl = allControls.find(c => c.id === savedControl.controlId);
                        if (!fullControl && !savedControl.isNewSuggestion) {
                            console.warn(`Control definition not found in allControls for ID: ${savedControl.controlId}`);
                            // Still return a basic object so responsible/decision might be linked
                        }
                        return {
                            id: savedControl.controlId,
                            name: savedControl.name || fullControl?.name || `Contrôle ${savedControl.controlId.substring(0, 6)}...`,
                            measure_type: savedControl.measure_type || fullControl?.measure_type || null, // Restore measure type
                            isNewSuggestion: savedControl.isNewSuggestion || false,
                            description: fullControl?.description || '',
                            type: fullControl?.type || (savedControl.isNewSuggestion ? 'AI' : 'Inconnu'),
                        };
                    }).filter(Boolean); // Filter out nulls from invalid entries

                    // Extract responsible/decision info
                    const responsiblesForThisKey = {};
                    const decisionsForThisKey = {};
                    entry.controls.forEach(control => {
                        // Ensure control.controlId exists before using it as a key
                        if (control.controlId) {
                            if (control.responsiblePerson) {
                                responsiblesForThisKey[control.controlId] = control.responsiblePerson;
                            }
                            if (control.decision) {
                                decisionsForThisKey[control.controlId] = {
                                    decision: control.decision,
                                    date: control.decisionDate || null
                                };
                            }
                        }
                    });
                    if (Object.keys(responsiblesForThisKey).length > 0) {
                        newResponsiblePersons[key] = responsiblesForThisKey;
                    }
                    if (Object.keys(decisionsForThisKey).length > 0) {
                        newImplementationDecisions[key] = decisionsForThisKey;
                    }
                }
            });

            // Set all states at once
            console.log("SC Effect: Setting derived states:", { newTreatmentOptions, newTreatmentJustifications, newResidualSeverities, newResponsiblePersons, newImplementationDecisions, newSelectedControls, newVraisemblanceInitiale, newVraisemblanceResiduelle });
            setTreatmentOptions(newTreatmentOptions);
            setTreatmentJustifications(newTreatmentJustifications);
            setResidualSeverities(newResidualSeverities);
            setResponsiblePersons(newResponsiblePersons);
            setImplementationDecisions(newImplementationDecisions);
            setSelectedControlsForRuleDE(newSelectedControls);
            setVraisemblanceInitiale(newVraisemblanceInitiale);
            setVraisemblanceResiduelle(newVraisemblanceResiduelle);

            // ✅ Mark data as loaded - auto-save is now safe
            markDataLoaded();

        } else {
            console.log("SC Effect: Conditions not met, resetting states.", {
                planObjectExists: !!planObject,
                isPlanObject: typeof planObject === 'object',
                allControlsExists: !!allControls
            });
            // Reset states if conditions aren't met
            setTreatmentOptions({});
            setResidualSeverities({});
            setResponsiblePersons({});
            setImplementationDecisions({});
            setSelectedControlsForRuleDE({});
            setVraisemblanceInitiale({});
            setVraisemblanceResiduelle({});
        }
    }, [currentAnalysisControlPlan, allControls]); // Depend on allControls as well


    // --- Handler Functions ---

    // Refresh Handler - Specifically refresh security framework and control plan data
    const handleRefreshData = async () => {
        if (!currentAnalysis?.id) {
            console.error('No analysis selected for refresh');
            showErrorToast('Aucune analyse sélectionnée pour l\'actualisation');
            return;
        }

        try {
            console.log('Refreshing security framework and control plan data...');
            showInfoToast('Actualisation des règles de sécurité en cours...');

            const result = await useAtelier1Store.getState().refreshFramework(currentAnalysis.id);

            if (result.success) {
                showSuccessToast('Règles de sécurité actualisées avec succès');
                console.log('Security framework and control plan data refreshed successfully');
            } else {
                showErrorToast('Erreur lors de l\'actualisation des règles de sécurité');
            }
        } catch (error) {
            console.error('Error refreshing security framework data:', error);
            showErrorToast('Erreur lors de l\'actualisation des règles de sécurité');
        }
    };

    // Control Selection Modal Handlers - Combined fetch and open logic
    const fetchAndOpenControlModal = async (ruleId, deId) => {
        if (!ruleId) {
            console.error("Cannot open control modal: Missing ruleId");
            showErrorToast("Erreur: Contexte manquant pour ouvrir le modal.");
            return;
        }

        // Normalize key — orphaned rules use __no_de__ consistently with row rendering
        const deIdPart = deId != null ? deId : '__no_de__';
        const key = `${ruleId}_${deIdPart}`;

        // Find the current rule and dreaded event objects
        // Orphans live in erPairs (not linkedPairs), so search both pair lists
        const findPair = (list) => list.find(p => {
            if (String(p.rule?.id) !== String(ruleId)) return false;
            if (deId != null) return String(p.de?.id) === String(deId);
            return p.de?.id == null;
        });
        const currentPair = findPair(erPairs) || findPair(nonIntentionalPairs);
        const currentRule = currentPair?.rule;
        const currentDE = currentPair?.de;

        if (!currentRule) {
            console.error("Cannot find rule objects", { ruleId, deId });
            showErrorToast("Erreur: Impossible de trouver les informations de contexte.");
            return;
        }

        // Get the treatment option for this rule-DE pair
        const currentTreatmentOption = treatmentOptions[key];

        // Get the security pillar directly from the dreaded event
        // Note: DreadedEvent has a single securityPillar property, not pillars (plural)
        const securityPillar = currentDE?.securityPillar || '';

        // Convert to array for consistent processing
        const securityPillars = securityPillar ? [securityPillar] : [];

        console.log('Context for control filtering:', {
            rule: currentRule.name,
            dreadedEvent: currentDE?.name || '(orphan)',
            treatmentOption: currentTreatmentOption,
            securityPillar,
            securityPillars
        });

        // Note: Security controls are filtered by the pillar of the dreaded event, not the business value

        setModalContext({ ruleId, deId });
        setIsModalLoading(true);
        setModalError(null);
        setModalAvailableControls([]); // Clear previous controls
        setIsControlModalOpen(true); // Open modal immediately to show loading state

        try {
            // Get controls (either from context or API)
            let availableControls = [];

            if (allControls && Array.isArray(allControls) && allControls.length > 0) {
                console.log('Using allControls from context:', allControls.length, 'controls available');
                availableControls = allControls;
            } else {
                // If allControls is empty, fetch directly from the API
                console.log('allControls is empty, fetching from API');
                const controls = await securityControlsService.getAvailableSecurityControls();

                if (controls && Array.isArray(controls) && controls.length > 0) {
                    console.log('Fetched', controls.length, 'controls from API');
                    availableControls = controls;
                } else {
                    console.warn('No controls available from API');
                    throw new Error("Aucun contrôle disponible. Veuillez contacter l'administrateur.");
                }
            }

            // Filter controls based on security pillars and treatment option
            let filteredControls = availableControls;

            // Map treatment option values to risk_treatment_strategy values
            const treatmentOptionMap = {
                'reduce': 'Réduire le risque',
                'transfer': 'Transférer le risque',
                'avoid': 'Éviter le risque',
                'accept': 'Accepter le risque'
            };

            // Filter by treatment option if specified
            if (currentTreatmentOption && treatmentOptionMap[currentTreatmentOption]) {
                const treatmentStrategy = treatmentOptionMap[currentTreatmentOption];
                filteredControls = filteredControls.filter(control =>
                    control.risk_treatment_strategy === treatmentStrategy
                );
                console.log(`Filtered by treatment option '${currentTreatmentOption}':`, filteredControls.length, 'controls remaining');
            }

            // Filter by security pillar from the dreaded event (not the business value)
            if (securityPillar) {
                // Map from the dreaded event's securityPillar format to the control's pillar format
                const pillarMap = {
                    // Map from DreadedEvent.securityPillar to SecurityControl.pillar
                    'confidentialite': 'Confidentialité',
                    'integrite': 'Intégrité',
                    'disponibilite': 'Disponibilité',
                    'tracabilite': 'Traçabilité',
                    'preuve': 'Preuve',
                    'auditabilite': 'Auditabilité',
                    'Auditabilite': 'Auditabilité',
                    // Also include direct mappings in case the format is already correct
                    'Confidentialité': 'Confidentialité',
                    'Intégrité': 'Intégrité',
                    'Disponibilité': 'Disponibilité',
                    'Traçabilité': 'Traçabilité',
                    'Preuve': 'Preuve',
                    'Auditabilité': 'Auditabilité'
                };

                // Get the French pillar name
                const frenchPillar = pillarMap[securityPillar] || securityPillar;

                // Filter controls that match the specified pillar
                filteredControls = filteredControls.filter(control =>
                    control.pillar === frenchPillar
                );
                console.log(`Filtered by pillar '${frenchPillar}':`, filteredControls.length, 'controls remaining');
            }

            if (filteredControls.length === 0) {
                console.warn('No controls match the filtering criteria, showing all controls instead');
                // If no controls match the criteria, show all controls as a fallback
                setModalAvailableControls(availableControls);
                showInfoToast("Aucun contrôle ne correspond exactement aux critères. Tous les contrôles sont affichés.");
            } else {
                setModalAvailableControls(filteredControls);
            }
        } catch (error) {
            console.error("Error fetching controls for modal:", error);
            setModalError(error.message || "Impossible de charger les contrôles.");
            // Keep modal open to show error
        } finally {
            setIsModalLoading(false);
        }
    };


    // SAVES MANUAL SELECTIONS - Merges with existing AI suggestions
    // This function now uses modalAvailableControls to find the full object
    const handleSaveControlSelection = (newlySelectedManualIds) => {
        if (!modalContext) return;
        const { ruleId, deId } = modalContext;
        const deIdPart = deId != null ? deId : '__no_de__';
        const key = `${ruleId}_${deIdPart}`;

        // Get full control objects for the newly selected manual IDs FROM THE MODAL'S DATA SOURCE
        const newlySelectedManualControls = newlySelectedManualIds
            .map(id => modalAvailableControls.find(c => c.id === id || c._id === id)) // Use modalAvailableControls, check both id and _id
            .filter(Boolean)
            // Map the fetched control structure to the structure expected by setSelectedControlsForRuleDE
            .map(fetchedControl => {
                // Log the control structure to debug
                console.log('Processing control:', fetchedControl);

                return {
                    id: fetchedControl.id || fetchedControl._id, // Use id or _id
                    name: fetchedControl.measure_description || fetchedControl.name || 'Contrôle sans nom', // Try different field names
                    isNewSuggestion: false, // Mark as not an AI suggestion
                    description: `Pilier: ${fetchedControl.pillar || 'Non spécifié'}, Traitement: ${fetchedControl.risk_treatment_strategy || 'Non spécifié'}`, // Combine info for description
                    type: 'Database', // Indicate source
                };
            });

        setSelectedControlsForRuleDE(prev => {
            const newState = { ...prev };
            const existingControls = prev[key] || [];

            // Keep existing AI suggestions and manual controls (not affected by modal)
            const existingNonModalControls = existingControls.filter(c => c.isNewSuggestion || c.type === 'Manual');

            // Combine existing non-modal controls with the *newly selected* knowledge base controls
            // Use a Map to handle potential duplicates if a control was somehow already added
            const combinedMap = new Map();
            existingNonModalControls.forEach(c => combinedMap.set(c.id, c));
            newlySelectedManualControls.forEach(c => combinedMap.set(c.id, c)); // Overwrites if manual ID matches AI ID (unlikely but safe)

            newState[key] = Array.from(combinedMap.values());

            // Cleanup if empty
            if (newState[key].length === 0) {
                delete newState[key];
            }

            console.log(`Updated controls for ${key} after manual save:`, newState[key]);
            return newState;
        });
        setModalContext(null);
        setIsControlModalOpen(false);
    };

    // Table Cell Input Handlers (Ensure these are defined)
    const handleResidualSeverityChange = (key, newSeverity) => {
        // Extract deId from the key
        const deId = key.split('_').pop(); // Assumes key format 'ruleId_deId'

        // Find the corresponding dreaded event
        const dreadedEvent = currentDreadedEvents.find(event => String(event.id) === String(deId));

        if (!dreadedEvent) {
            console.warn(`Could not find dreaded event with ID ${deId} for key ${key}`);
            // Optionally allow update anyway or handle error
            setResidualSeverities(prev => ({ ...prev, [key]: newSeverity }));
            return;
        }

        const initialSeverity = dreadedEvent.severity;

        // Get numerical values for comparison
        const initialSeverityValue = SEVERITY_LEVEL_MAP[initialSeverity?.toLowerCase()] || 0;
        const newSeverityValue = SEVERITY_LEVEL_MAP[newSeverity?.toLowerCase()] || 0;

        // Validate: New severity must be <= initial severity
        if (newSeverityValue <= initialSeverityValue) {
            setResidualSeverities(prev => ({ ...prev, [key]: newSeverity }));
        } else {
            console.warn(`Validation failed: Residual severity (${newSeverity} / ${newSeverityValue}) cannot be higher than initial severity (${initialSeverity} / ${initialSeverityValue}) for key ${key}.`);
            // Optionally, add user feedback here (e.g., alert, toast)
            // alert(`La sévérité résiduelle ne peut pas être supérieure à la sévérité initiale (${initialSeverity}).`);
        }
    };

    const handleResponsibleChange = (key, controlId, value) => {
        // Set the selected responsible person directly
        setResponsiblePersons(prev => ({
            ...prev,
            [key]: {
                ...(prev[key] || {}),
                [controlId]: value
            }
        }));
    };



    const handleDecisionChange = (key, controlId, decision, date = null) => {
        setImplementationDecisions(prev => ({
            ...prev,
            [key]: {
                ...(prev[key] || {}),
                [controlId]: {
                    decision,
                    date: decision === 'apply_before' ? date : null
                }
            }
        }));
    };

    const handleTreatmentOptionChange = (key, optionValue) => {
        setTreatmentOptions(prev => ({ ...prev, [key]: optionValue }));
    };

    const handleJustificationChange = (key, value) => {
        setTreatmentJustifications(prev => ({ ...prev, [key]: value }));
        markModified();
    };

    const handleVraisemblanceInitialeChange = (key, value) => {
        const numValue = parseInt(value, 10);
        if (numValue >= 1 && numValue <= 4) {
            setVraisemblanceInitiale(prev => ({ ...prev, [key]: numValue }));
            setVraisemblanceResiduelle(prev => {
                if ((prev[key] || 0) > numValue) {
                    return { ...prev, [key]: numValue };
                }
                return prev;
            });
        }
    };

    const handleVraisemblanceResiduelleChange = (key, value) => {
        const numValue = parseInt(value, 10);
        const initiale = vraisemblanceInitiale[key] || 4;
        if (numValue >= 1 && numValue <= initiale) {
            setVraisemblanceResiduelle(prev => ({ ...prev, [key]: numValue }));
        }
    };

    // AI Suggestion Modal Handlers
    const handleOpenAiSuggestionModal = (rule, de) => {
        if (!rule) { console.error("Missing context for AI modal."); return; }
        setAiModalContext({ rule, de });
        setIsAiSuggestionModalOpen(true);

        const deIdPart = de?.id != null ? de.id : '__no_de__';
        const key = `${rule.id}_${deIdPart}`;

        // Check cache
        if (aiSuggestionsCache[key]) {
            setAiSuggestionsList(aiSuggestionsCache[key]);
        } else {
            setAiSuggestionsList([]);
        }

        setSelectedAiSuggestionNames([]);
        setIsLoadingAiSuggestions(false);
        setAiSuggestionError(null);
    };

    const handleCloseAiSuggestionModal = () => {
        setIsAiSuggestionModalOpen(false); // Other resets happen in handleAdd or handleOpen
    };

    const handleGenerateAiSuggestions = async () => {
        if (!aiModalContext) return;
        setIsLoadingAiSuggestions(true);
        setAiSuggestionsList([]);
        setSelectedAiSuggestionNames([]);
        setAiSuggestionError(null);

        const { rule, de } = aiModalContext;
        const deIdPart = de?.id != null ? de.id : '__no_de__';
        const key = `${rule.id}_${deIdPart}`;

        const treatment = treatmentOptions[key] || null;
        // Find the full business value object using the ID stored in the dreaded event
        const bv = de?.businessValue ? currentBusinessValues?.find(b => String(b.id) === String(de.businessValue)) : null;
        const supportAssets = bv?.supportAssets || [];
        const existingControlsForKey = selectedControlsForRuleDE[key] || [];
        const existingControlNames = existingControlsForKey.map(c => c.name);

        const apiContext = {
            ruleName: rule.name,
            ruleDescription: rule.description,
            dreadedEventName: de?.name || '(Règle orpheline)',
            dreadedEventSeverity: de?.severity || null,
            treatmentOption: treatment,
            businessValueName: bv?.name || null,
            businessValueDescription: bv?.description || null,
            supportAssetNames: supportAssets.map(sa => sa.name),
            existingControlNames: existingControlNames,
            language: 'fr',
            analysisId: currentAnalysis?.id // Pass analysisId for permission checking
        };

        // console.log("Sending ENHANCED context to AI service:", apiContext);

        try {
            const suggestions = await securityControlsService.getAiControlSuggestions(apiContext);
            // console.log("Received AI suggestions:", suggestions);
            // Filter out suggestions that *exactly* match existing control names (case-insensitive)
            const uniqueSuggestions = suggestions.filter(suggestion => {
                // Handle both old format (strings) and new format (objects)
                const suggestionName = typeof suggestion === 'string' ? suggestion : suggestion.name;
                return !existingControlNames.some(existingName =>
                    existingName.trim().toLowerCase() === suggestionName.trim().toLowerCase()
                );
            });

            setAiSuggestionsList(uniqueSuggestions || []);

            // Update Cache
            setAiSuggestionsCache(prev => ({
                ...prev,
                [key]: uniqueSuggestions || []
            }));

            // if (uniqueSuggestions.length < suggestions.length) { console.log("Filtered out duplicate AI suggestions."); }
        } catch (error) {
            console.error("Error fetching AI suggestions:", error);
            // Use centralized AI error handler for user-friendly messages
            const errorResult = handleAIError(error, { language: 'fr' });
            setAiSuggestionError(errorResult?.error?.userMessage?.message || "Erreur lors de la récupération des suggestions IA.");
            setAiSuggestionsList([]);
        } finally {
            setIsLoadingAiSuggestions(false);
        }
    };

    const handleSelectAiSuggestion = (suggestion) => {
        // Handle both old format (strings) and new format (objects)
        const suggestionKey = typeof suggestion === 'string' ? suggestion : suggestion.name;

        setSelectedAiSuggestionNames(prev => {
            // Check if this suggestion is already selected (by name for compatibility)
            const isAlreadySelected = prev.some(selected => {
                const selectedKey = typeof selected === 'string' ? selected : selected.name;
                return selectedKey === suggestionKey;
            });

            if (isAlreadySelected) {
                // Remove the suggestion
                return prev.filter(selected => {
                    const selectedKey = typeof selected === 'string' ? selected : selected.name;
                    return selectedKey !== suggestionKey;
                });
            } else {
                // Add the suggestion (keep original format)
                return [...prev, suggestion];
            }
        });
    };

    // ADDS SELECTED AI SUGGESTIONS - Merges with existing manual selections
    const handleAddSelectedAiControls = () => {
        if (!aiModalContext || selectedAiSuggestionNames.length === 0) return;

        const { rule, de } = aiModalContext;
        // Ensure the key uses rule.id to match the rendering logic and save logic
        const ruleIdForKey = rule?.id;
        const deIdForKey = de?.id;

        // Check if IDs are valid before creating the key
        if (!ruleIdForKey || !deIdForKey) {
            console.error("Cannot add AI controls: Missing rule or DE ID.", { rule, de });
            handleCloseAiSuggestionModal();
            return;
        }

        const key = `${ruleIdForKey}_${deIdForKey}`;
        console.log(`SC Add AI: Targeting key: ${key}`); // Debug log

        // Create new control *objects* for the AI suggestions
        // Handle both old format (strings) and new format (objects with name and measure_type)
        const newAiControlsToAdd = selectedAiSuggestionNames.map(suggestion => {
            // Check if suggestion is a string (old format) or object (new format)
            const suggestionName = typeof suggestion === 'string' ? suggestion : suggestion.name;
            const measureType = typeof suggestion === 'object' ? suggestion.measure_type : null;

            return {
                id: `ai_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`, // Unique temporary ID
                name: suggestionName,
                measure_type: measureType, // Add measure type if available
                isNewSuggestion: true,
                // Add default properties if needed for display/logic
                description: 'Suggestion générée par IA.',
                type: 'AI',
            };
        });
        console.log("SC Add AI: New AI controls to add:", newAiControlsToAdd); // Debug log

        setSelectedControlsForRuleDE(prev => {
            const newState = { ...prev };
            const existingControls = prev[key] || [];
            console.log(`SC Add AI: Existing controls for key ${key}:`, existingControls); // Debug log

            // Keep ALL existing controls (both manual and AI)
            // Use a Map to prevent adding duplicate AI suggestions (based on name) if button clicked multiple times
            const combinedMap = new Map();

            // First add all existing controls (both manual and AI)
            existingControls.forEach(c => combinedMap.set(c.id, c));

            // Add new AI suggestions, checking against existing names (case-insensitive)
            const existingNamesLower = new Set(existingControls.map(c => c.name.trim().toLowerCase()));
            newAiControlsToAdd.forEach(aiControl => {
                if (!existingNamesLower.has(aiControl.name.trim().toLowerCase())) {
                    combinedMap.set(aiControl.id, aiControl);
                } else {
                    console.log(`Skipping duplicate AI suggestion: ${aiControl.name}`);
                }
            });

            newState[key] = Array.from(combinedMap.values());

            // Cleanup if empty (shouldn't happen here as we check selectedAiSuggestionNames.length)
            // if (newState[key].length === 0) { delete newState[key]; }

            console.log(`SC Add AI: Updated controls for key ${key}:`, newState[key]); // Debug log
            return newState;
        });

        handleCloseAiSuggestionModal(); // Close modal after adding
    };


    // --- Global Save Handler ---
    const handleGlobalSave = async () => {
        setIsLoadingSave(true);
        justSaved.current = true; // Flag that a save was just initiated
        // console.log("Attempting to save control plan...");

        const planToSave = {};
        // ---> Iterate over the LINKED PAIRS instead of all rules/events <---
        // Save ALL linked pairs to preserve rule-dreaded event relationships,
        // even if no controls have been selected yet
        linkedPairs.forEach(({ rule, de }) => {
            // Use rule.id (established in SecurityFramework) and de.id for consistency
            const ruleIdForKey = rule?.id;
            const deIdForKey = de?.id;

            // Skip if IDs are invalid (shouldn't happen based on linkedPairs calculation, but good practice)
            if (!ruleIdForKey || !deIdForKey) {
                // Removed sensitive data logging for security
                return; // Skip this pair
            }

            const key = `${ruleIdForKey}_${deIdForKey}`;

            const controlsForKey = selectedControlsForRuleDE[key]; // Array of objects
            const treatment = treatmentOptions[key];
            const justification = treatmentJustifications[key];
            const residualSev = residualSeverities[key];

            // Always save the entry to preserve rule-dreaded event relationships
            // Even if no controls, treatment, or residual severity are defined yet
            planToSave[key] = {
                ruleId: ruleIdForKey, // Use the consistent ID
                dreadedEventId: deIdForKey,
                treatmentOption: treatment || null,
                treatmentJustification: justification || null,
                residualSeverity: residualSev || null,
                vraisemblanceInitiale: vraisemblanceInitiale[key] || null,
                vraisemblanceResiduelle: vraisemblanceResiduelle[key] || null,
                // Map control objects back to the structure expected by API/storage
                controls: controlsForKey ? controlsForKey.map(control => ({
                    controlId: control.id, // Use the actual control ID (manual or temporary AI)
                    name: control.name,    // Save name for context
                    measure_type: control.measure_type || null, // Save measure type
                    isNewSuggestion: control.isNewSuggestion || false, // Persist AI suggestion flag
                    responsiblePerson: responsiblePersons?.[key]?.[control.id] || null,
                    decision: implementationDecisions?.[key]?.[control.id]?.decision || null,
                    decisionDate: implementationDecisions?.[key]?.[control.id]?.date || null,
                })) : []
            };
        });

        // Show loading toast
        const toastId = showLoadingToast(t('securityControls.buttons.saving'));

        try {
            // Removed sensitive data logging for security
            await useAtelier1Store.getState().saveControlPlan({
                plan: planToSave,
                aiSuggestionsCache: globalAiCache
            }, broadcastUpdate);
            // Removed sensitive data logging for security

            // Update loading toast to success
            updateToast(toastId, t('securityControls.status.planSaved'), 'success');
        } catch (error) {
            // Log without revealing sensitive data
            console.error('Error saving security controls plan:', error);
        } finally {
            setIsLoadingSave(false);
            setTimeout(() => { justSaved.current = false; }, 100); // Reset after a short delay
        }
    };

    // Silent save function for auto-save (no toast notifications)
    // GUARDED: Will only save if data was loaded AND user made modifications
    const handleSilentSave = async () => {
        // ⛔ Safety guard: don't save if data hasn't loaded or user hasn't changed anything
        if (!canAutoSave()) {
            return 'skipped'; // Signal to parent that no save was needed
        }

        const planToSave = {};
        linkedPairs.forEach(({ rule, de }) => {
            const ruleIdForKey = rule?.id;
            const deIdForKey = de?.id;
            if (!ruleIdForKey || !deIdForKey) return;

            const key = `${ruleIdForKey}_${deIdForKey}`;
            const controlsForKey = selectedControlsForRuleDE[key];
            const treatment = treatmentOptions[key];
            const justification = treatmentJustifications[key];
            const residualSev = residualSeverities[key];

            planToSave[key] = {
                ruleId: ruleIdForKey,
                dreadedEventId: deIdForKey,
                treatmentOption: treatment || null,
                treatmentJustification: justification || null,
                residualSeverity: residualSev || null,
                vraisemblanceInitiale: vraisemblanceInitiale[key] || null,
                vraisemblanceResiduelle: vraisemblanceResiduelle[key] || null,
                controls: controlsForKey ? controlsForKey.map(control => ({
                    controlId: control.id,
                    name: control.name,
                    measure_type: control.measure_type || null,
                    isNewSuggestion: control.isNewSuggestion || false,
                    responsiblePerson: responsiblePersons?.[key]?.[control.id] || null,
                    decision: implementationDecisions?.[key]?.[control.id]?.decision || null,
                    decisionDate: implementationDecisions?.[key]?.[control.id]?.date || null,
                })) : []
            };
        });

        try {
            await useAtelier1Store.getState().saveControlPlan({
                plan: planToSave,
                aiSuggestionsCache: globalAiCache
            }, broadcastUpdate);
            clearModified();
            return true;
        } catch (error) {
            console.error('Silent save error security controls:', error);
            return false;
        } finally {
            setTimeout(() => { justSaved.current = false; }, 100);
        }
    };

    useSilentSaveListener('security-controls', handleSilentSave);


    // PDF Export Handler
    const handleExportPDF = async () => {
        try {
            showLoadingToast('Génération du rapport PDF en cours...');

            const generator = new SecurityControlsPdfGenerator(
                currentAnalysisControlPlan,
                currentAnalysis,
                currentSecurityFramework,  // Pass security framework for rule names
                currentDreadedEvents       // Pass dreaded events for event names
            );

            const blob = await generator.generateSecurityControlsReport();

            // Download the PDF
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `mesures-de-controle-${currentAnalysis?.name || 'rapport'}.pdf`;
            link.click();
            URL.revokeObjectURL(url);

            showSuccessToast('Rapport PDF généré avec succès');
        } catch (error) {
            console.error('Error generating PDF:', error);
            showErrorToast('Erreur lors de la génération du PDF');
        }
    };

    // PDF Export Handler (English)
    const handleExportPDFEn = async () => {
        try {
            showLoadingToast('Generating PDF Report (EN)...');

            const generator = new SecurityControlsPdfGenerator(
                currentAnalysisControlPlan,
                currentAnalysis,
                currentSecurityFramework,  // Pass security framework for rule names
                currentDreadedEvents,       // Pass dreaded events for event names
                'en'
            );

            const blob = await generator.generateSecurityControlsReport();

            // Download the PDF
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `security-controls-${currentAnalysis?.name || 'report'}_EN.pdf`;
            link.click();
            URL.revokeObjectURL(url);

            showSuccessToast('PDF Report (EN) generated successfully');
        } catch (error) {
            console.error('Error generating PDF (EN):', error);
            showErrorToast('Error generating PDF (EN)');
        }
    };

    // DOCX Export Handler
    const handleExportDocx = async () => {
        try {
            showLoadingToast('Génération du rapport Word en cours...');

            const { generateSecurityControlsDocx } = await import('../../../utils/securityControlsDocxGenerator.js');

            await generateSecurityControlsDocx(
                currentAnalysisControlPlan,
                currentAnalysis,
                currentSecurityFramework,  // Pass security framework for rule names
                currentDreadedEvents       // Pass dreaded events for event names
            );

            showSuccessToast('Rapport Word généré avec succès');
        } catch (error) {
            console.error('Error generating DOCX:', error);
            showErrorToast('Erreur lors de la génération du Word');
        }
    };

    // DOCX Export Handler (English)
    const handleExportDocxEn = async () => {
        try {
            showLoadingToast('Generating Word report...');

            const { generateSecurityControlsDocx } = await import('../../../utils/securityControlsDocxGenerator.js');

            await generateSecurityControlsDocx(
                currentAnalysisControlPlan,
                currentAnalysis,
                currentSecurityFramework,  // Pass security framework for rule names
                currentDreadedEvents,      // Pass dreaded events for event names
                'en'
            );

            showSuccessToast('Word Report (EN) generated successfully');
        } catch (error) {
            console.error('Error generating DOCX (EN):', error);
            showErrorToast('Error generating English Word file');
        }
    };

    // --- Global AI Generation Handlers ---

    const handleGlobalAiGeneration = async (forceRegenerate = false) => {
        console.log("🔍 handleGlobalAiGeneration called with forceRegenerate:", forceRegenerate);
        console.log("🔍 Current globalAiCache:", globalAiCache);
        console.log("🔍 Cache keys count:", Object.keys(globalAiCache).length);

        // Check if we have cached suggestions in database
        if (!forceRegenerate && Object.keys(globalAiCache).length > 0) {
            console.log("✅ Found suggestions in database, showing them - NO API CALLS");
            // Set states in correct order - loading false FIRST
            setIsGlobalAiLoading(false);
            setGlobalAiError(null);
            setGlobalAiSuggestions(globalAiCache);
            // Small delay to ensure state updates before opening modal
            setTimeout(() => {
                setIsGlobalAiModalOpen(true);
            }, 0);
            return; // IMPORTANT: Exit here, don't continue to API calls
        }

        // No cache or forcing regenerate - call AI
        console.log("🤖 No cached suggestions found OR forcing regenerate, calling AI model...");
        console.log("🚀 Using BATCH endpoint - single API call for", linkedPairs.length, "rules");
        setIsGlobalAiLoading(true);
        setGlobalAiError(null);
        setGlobalAiSuggestions({});
        setIsGlobalAiModalOpen(true); // Open modal immediately to show loading

        try {
            // Build batch request - gather all rules that need suggestions
            const rulesToProcess = [];
            const cachedResults = {};

            for (const { rule, de } of linkedPairs) {
                const key = `${rule.id}_${de.id}`;

                // Check if we have cached suggestions for this specific rule (unless forcing regenerate)
                if (!forceRegenerate && globalAiCache[key]) {
                    console.log(`✅ Using cached suggestions for ${key}`);
                    cachedResults[key] = globalAiCache[key];
                    continue; // Skip this rule - we have cached data
                }

                // Prepare context for this rule
                const existingControls = selectedControlsForRuleDE[key] || [];
                const treatment = treatmentOptions[key] || null;
                const bv = currentBusinessValues?.find(b => String(b.id) === String(de.businessValue));
                const supportAssets = bv?.supportAssets || [];
                const existingControlNames = existingControls.map(c => c.name);

                // Helper to truncate text to reduce payload size
                const truncate = (text, maxLen = 150) =>
                    text && text.length > maxLen ? text.substring(0, maxLen) + '...' : text;

                rulesToProcess.push({
                    key,
                    ruleName: rule.name,
                    ruleDescription: truncate(rule.description, 150),
                    dreadedEventName: de.name,
                    dreadedEventSeverity: de.severity,
                    treatmentOption: treatment,
                    businessValueName: bv?.name || null,
                    businessValueDescription: truncate(bv?.description, 150),
                    supportAssetNames: supportAssets.map(sa => sa.name),
                    existingControlNames: existingControlNames.slice(0, 5) // Limit to 5 existing control names
                });
            }

            let newSuggestionsMap = { ...cachedResults };

            // Only call API if there are rules that need processing
            if (rulesToProcess.length > 0) {
                console.log(`🚀 Sending BATCH request with ${rulesToProcess.length} rules`);

                try {
                    // Single API call for all rules!
                    const batchSuggestions = await securityControlsService.getAiControlSuggestionsBatch(
                        rulesToProcess,
                        'fr',
                        currentAnalysis?.id // Pass analysisId for permission checking
                    );

                    // Process batch results
                    Object.entries(batchSuggestions).forEach(([key, suggestions]) => {
                        // Filter duplicates based on existing controls
                        const ruleContext = rulesToProcess.find(r => r.key === key);
                        const existingNames = ruleContext?.existingControlNames || [];

                        const uniqueSuggestions = suggestions.filter(suggestion => {
                            const suggestionName = typeof suggestion === 'string' ? suggestion : suggestion.name;
                            return !existingNames.some(existingName =>
                                existingName.trim().toLowerCase() === suggestionName.trim().toLowerCase()
                            );
                        });

                        // Limit to 3 suggestions per rule
                        const limitedSuggestions = uniqueSuggestions.slice(0, 3);

                        if (limitedSuggestions.length > 0) {
                            newSuggestionsMap[key] = limitedSuggestions;
                        }
                    });

                    console.log(`✅ Batch processing complete: ${Object.keys(newSuggestionsMap).length} rules have suggestions`);
                } catch (err) {
                    console.error("❌ Batch AI request failed:", err);
                    setGlobalAiError("Erreur lors de la génération des suggestions IA. Veuillez réessayer.");
                }
            } else {
                console.log("✅ All rules were served from cache, no API call needed");
            }

            setGlobalAiSuggestions(newSuggestionsMap);
            setGlobalAiCache(newSuggestionsMap); // Update cache with new results

            // Auto-save cache to database
            try {
                console.log("💾 Auto-saving AI suggestions cache to database...");
                await useAtelier1Store.getState().saveControlPlan({
                    plan: Object.fromEntries(
                        Object.entries(selectedControlsForRuleDE).map(([key, controls]) => {
                            const [ruleId, deId] = key.split('_');
                            return [key, {
                                ruleId,
                                dreadedEventId: deId,
                                treatmentOption: treatmentOptions[key] || null,
                                residualSeverity: residualSeverities[key] || null,
                                controls: controls.map(control => ({
                                    controlId: control.id,
                                    name: control.name,
                                    measure_type: control.measure_type || null,
                                    isNewSuggestion: control.isNewSuggestion || false,
                                    responsiblePerson: responsiblePersons?.[key]?.[control.id] || null,
                                    decision: implementationDecisions?.[key]?.[control.id]?.decision || null,
                                    decisionDate: implementationDecisions?.[key]?.[control.id]?.date || null,
                                }))
                            }];
                        })
                    ),
                    aiSuggestionsCache: newSuggestionsMap
                }, broadcastUpdate);
                console.log("✅ AI cache saved to database successfully");
            } catch (saveError) {
                console.error("❌ Failed to auto-save AI cache:", saveError);
                // Don't throw - cache is still in memory
            }

            if (Object.keys(newSuggestionsMap).length === 0) {
                // Optional: set a message if no new suggestions found
            }

        } catch (error) {
            console.error("Global AI generation error:", error);
            // Use centralized AI error handler for user-friendly messages
            const errorResult = handleAIError(error, { language: 'fr' });
            setGlobalAiError(errorResult?.error?.userMessage?.message || "Une erreur est survenue lors de la génération globale.");
        } finally {
            setIsGlobalAiLoading(false);
        }
    };

    const handleAcceptGlobalSuggestions = (acceptedSuggestionsMap) => {
        // acceptedSuggestionsMap: { [key]: [suggestionObjects] }

        setSelectedControlsForRuleDE(prev => {
            const newState = { ...prev };

            Object.entries(acceptedSuggestionsMap).forEach(([key, suggestions]) => {
                const existingControls = newState[key] || [];
                const existingNamesLower = new Set(existingControls.map(c => c.name.trim().toLowerCase()));

                const newControlsToAdd = suggestions.map(suggestion => {
                    const suggestionName = typeof suggestion === 'string' ? suggestion : suggestion.name;
                    const measureType = typeof suggestion === 'object' ? suggestion.measure_type : null;

                    return {
                        id: `ai_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
                        name: suggestionName,
                        measure_type: measureType,
                        isNewSuggestion: true,
                        description: 'Suggestion générée par IA (Global).',
                        type: 'AI',
                    };
                }).filter(c => !existingNamesLower.has(c.name.trim().toLowerCase()));

                if (newControlsToAdd.length > 0) {
                    newState[key] = [...existingControls, ...newControlsToAdd];
                }
            });

            return newState;
        });

        setIsGlobalAiModalOpen(false);
        showSuccessToast(t('securityControls.status.suggestionsAdded', 'Suggestions ajoutées avec succès'));
    };


    return (
        <div ref={formChangeRef} className="space-y-8 p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
            {/* === MERGED HEADER === */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    {/* Left Side: Breadcrumb & Title */}
                    <div>
                        <div className="flex items-center text-sm text-slate-500 mb-2">
                            <span className="hover:text-blue-600 transition-colors">{t('securityControls.breadcrumb.workshop1')}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span className="text-blue-600 font-medium">{t('securityControls.breadcrumb.securityControls')}</span>
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center">
                            <ShieldCheck size={28} className="mr-3 text-blue-600" />
                            {t('securityControls.title')}
                        </h1>
                    </div>

                    {/* Right Side: Buttons */}
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Refresh Button */}
                        <Button
                            variant="secondary"
                            color="white"
                            icon={<RefreshCw />}
                            onClick={handleRefreshData}
                            disabled={analysisStore.isWorkshopDataLoading}
                            loading={analysisStore.isWorkshopDataLoading}
                            title={t('securityControls.tooltips.refresh')}
                        >
                            {analysisStore.isWorkshopDataLoading ? t('securityControls.buttons.refreshing') : t('securityControls.buttons.refresh')}
                        </Button>

                        {/* Global AI Generation Button - Icon Only */}
                        {companyAiEnabled && (
                        <Button
                            variant="secondary"
                            color="indigo"
                            icon={<BrainCircuit />}
                            onClick={() => handleGlobalAiGeneration()}
                            title={t('securityControls.buttons.generateAllAi', 'Générer des suggestions IA pour toutes les règles')}
                            disabled={isGlobalAiLoading || analysisStore.isWorkshopDataLoading}
                        />
                        )}
                        {/* Icon-only button - no text */}

                        {/* Unified Export Menu */}
                        <WorkshopExportMenu
                            onExportPdfFr={handleExportPDF}
                            onExportPdfEn={handleExportPDFEn}
                            onExportDocxFr={handleExportDocx}
                            onExportDocxEn={handleExportDocxEn}
                        />

                        {/* Save Button */}
                        <Button
                            variant="primary"
                            color="blue"
                            icon={<Save />}
                            onClick={handleGlobalSave}
                            disabled={isLoadingSave}
                            loading={isLoadingSave}
                        >
                            {isLoadingSave ? t('securityControls.buttons.saving') : t('securityControls.buttons.save')}
                        </Button>
                    </div>
                </div>
            </div>
            {/* === END MERGED HEADER === */}

            {/* Info Boxes - Hidden on Print */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 print:hidden">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 shadow-sm">
                    <div className="flex items-center text-blue-700"> <Info size={18} className="mr-2" /> <h3 className="font-medium">{t('securityControls.sections.securityRules')}</h3> </div>
                    <p className="text-blue-600 text-sm mt-1"> {linkedPairs && linkedPairs.length > 0 ? [...new Set(linkedPairs.map(pair => pair.rule?.id))].length : 0} {t('securityControls.status.rulesDefined')} </p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-100 shadow-sm">
                    <div className="flex items-center text-emerald-700"> <Info size={18} className="mr-2" /> <h3 className="font-medium">{t('securityControls.sections.identifiedControls')}</h3> </div>
                    <p className="text-emerald-600 text-sm mt-1"> {Object.values(selectedControlsForRuleDE).flat().length || 0} {t('securityControls.status.controlsIdentified')} </p>
                </div>
            </div>

            {/* Main Table Container */}
            <div className="overflow-x-auto">
                <table id="security-controls-table" className="min-w-full border-collapse border border-gray-300">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="border px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider print:py-1 print:px-1.5 w-[18%]">{t('securityControls.table.headers.rule')}</th>
                            <th className="border px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider print:py-1 print:px-1.5 w-[15%]">{t('securityControls.table.headers.fearedEvent')}</th>
                            <th className="border px-3 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider print:py-1 print:px-1.5 w-[8%]">{t('securityControls.table.headers.severity')}</th>
                            <th className="border px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider print:py-1 print:px-1.5 w-[35%]" colSpan="3">{t('securityControls.table.headers.controls')}</th>
                            <th className="border px-3 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider print:py-1 print:px-1.5 w-[10%]">{t('securityControls.table.headers.residualSeverity')}</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {/* ---> Iterate over erPairs <--- */}
                        {/* Map with index to check previous row */}
                        {erPairs && erPairs.length > 0 ? (
                            erPairs.map(({ rule, de }, index) => {

                                const isNoDe = de?.id === null || de?.id === '__v';
                                const ruleIdForKey = rule?.id || `rule-${index}`;
                                const deIdForKey = isNoDe ? '__no_de__' : (de?.id || `de-${index}`);
                                const key = `${ruleIdForKey}_${deIdForKey}`;

                                // ---> Check if it's the start of a new rule group <---
                                const isNewRuleGroup = index === 0 || (rule?.id && erPairs[index - 1]?.rule?.id !== rule.id);

                                // ---> Calculate rowSpan if it's a new group <---
                                let ruleRowSpan = 1;
                                if (isNewRuleGroup && rule?.id) {
                                    for (let i = index + 1; i < erPairs.length; i++) {
                                        if (erPairs[i]?.rule?.id === rule.id) {
                                            ruleRowSpan++;
                                        } else {
                                            break; // Stop counting when rule ID changes
                                        }
                                    }
                                }

                                const severityInfo = !isNoDe ? getSeverityLevelInfo(de.severity, t) : { level: '-', label: '-', textClass: 'text-gray-400', bgClass: 'bg-gray-100' };
                                const residualSeverity = residualSeverities[key];
                                const residualSeverityInfo = residualSeverity ? getSeverityLevelInfo(residualSeverity, t) : { level: '', label: '', textClass: 'text-gray-500', bgClass: 'bg-gray-100' };
                                const selectedControls = selectedControlsForRuleDE[key] || []; // Array of objects
                                const currentTreatmentOption = treatmentOptions[key];

                                // Get initial severity value for filtering residual options
                                const initialSeverityValue = isNoDe ? 0 : (SEVERITY_LEVEL_MAP[de.severity?.toLowerCase()] || 0);
                                const severityOptions = getSeverityOptions(t);
                                const filteredSeverityOptions = severityOptions.filter(opt =>
                                    (SEVERITY_LEVEL_MAP[opt.value?.toLowerCase()] || 0) <= initialSeverityValue
                                );

                                // Map Tailwind class to a print-specific class
                                const printSeverityBgClass = `print:${severityInfo.bgClass}`;
                                const printSeverityTextClass = `print:${severityInfo.textClass}`;
                                const printResidualTextClass = `print:${residualSeverityInfo.textClass}`;

                                // Determine row background based on severity change
                                let rowBgClass = 'bg-white hover:bg-gray-50 transition-colors duration-150';
                                if (!isNoDe) {
                                    const initialSevMapVal = SEVERITY_LEVEL_MAP[de.severity?.toLowerCase()];
                                    const residualSevMapVal = SEVERITY_LEVEL_MAP[residualSeverity?.toLowerCase()];
                                    if (initialSevMapVal && residualSevMapVal) {
                                        if (residualSevMapVal < initialSevMapVal) rowBgClass = 'bg-green-50 hover:bg-green-100 transition-colors duration-150';
                                        else if (residualSevMapVal === initialSevMapVal) rowBgClass = 'bg-yellow-50 hover:bg-yellow-100 transition-colors duration-150';
                                    }
                                }

                                // Find linked Business Value Name (using de.businessValue ID)
                                const linkedBv = Array.isArray(currentBusinessValues)
                                    ? currentBusinessValues.find(bv => String(bv.id) === String(de.businessValue))
                                    : null;
                                const linkedBvName = linkedBv?.name;

                                return (
                                    // Each row is now a direct rule-event pair
                                    <tr key={key} className={`${rowBgClass}`}>
                                        {/* Conditionally render Rule cell with rowSpan */}
                                        {isNewRuleGroup && (
                                            <td className="border px-3 py-3 align-top print:py-1 print:px-1.5" rowSpan={ruleRowSpan}>
                                                <div className="font-medium text-sm text-gray-800 mb-1" title={rule?.description}>{rule?.name || t('securityControls.status.unknownRule')}</div>
                                                {(() => {
                                                    const statusLevel = normalizeRuleStatus(rule?.status);
                                                    const config = getRuleStatusConfig(statusLevel, t);
                                                    return (
                                                        <div className="space-y-0.5">
                                                            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-0.5">{t('securityFoundation.sections.status')}</span>
                                                            <span className={`inline-flex items-center text-xs leading-5 font-semibold rounded-full px-2 py-0.5 ${config.bgClass} ${config.textClass}`}>
                                                                {config.label}
                                                            </span>
                                                            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mt-1 mb-0.5">{t('securityFoundation.sections.justification')}</span>
                                                            <div className="text-xs text-gray-400">
                                                                {rule?.justification || <span className="italic">—</span>}
                                                            </div>
                                                            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mt-1 mb-0.5">{t('securityFoundation.summary.headers.gapComment')}</span>
                                                            <div className="text-xs text-gray-500 italic break-words">
                                                                {ruleGapComments[rule?.id] ? ruleGapComments[rule.id] : <span className="italic">—</span>}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                        )}
                                        {/* Feared Event */}
                                        <td className="border px-3 py-3 align-middle print:py-1 print:px-1.5">
                                            {isNoDe ? (
                                                <span className="text-sm text-gray-400">—</span>
                                            ) : (
                                                <span className="text-sm font-medium text-gray-800">{de?.name || t('securityControls.status.unknownEvent')}</span>
                                            )}
                                        </td>
                                        {/* Initial Severity */}
                                        <td className={`border px-3 py-3 text-center align-middle print:py-1 print:px-1.5 ${isNoDe ? 'bg-gray-100' : severityInfo.bgClass} ${isNoDe ? '' : printSeverityBgClass}`}>
                                            {isNoDe ? (
                                                <span className="text-sm text-gray-400">—</span>
                                            ) : (
                                                <span className={`text-xs font-medium ${severityInfo.textClass} ${printSeverityTextClass}`}> {severityInfo.label} ({severityInfo.level}) </span>
                                            )}
                                        </td>
                                        {/* --- Combined Controls/Responsible/Decision Column --- */}
                                        <td className="border px-1 py-1 align-top print:py-0.5 print:px-0.5" colSpan="3">
                                            {['transfer', 'avoid', 'accept'].includes(currentTreatmentOption) ? (
                                                <div className="p-2 h-full flex flex-col">
                                                    <label className="text-xs font-medium text-gray-700 mb-1">{t('securityControls.sections.justification')}</label>
                                                    <textarea
                                                        className="w-full flex-grow p-2 border border-gray-300 rounded text-xs focus:ring-blue-500 focus:border-blue-500 resize-none print:hidden min-h-[80px]"
                                                        value={treatmentJustifications[key] || ''}
                                                        onChange={(e) => handleJustificationChange(key, e.target.value)}
                                                        placeholder={t('securityControls.placeholders.justificationPlaceholder', 'Justifiez votre choix de traitement...')}
                                                    />
                                                    <div className="hidden print:block text-xs mt-1 whitespace-pre-wrap">
                                                        {treatmentJustifications[key] || <span className="italic text-gray-400">Aucune justification</span>}
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="flex items-center gap-2 mb-2 print:hidden print-internal-hidden">
                                                {/* Database Controls Button with Label */}
                                                <button
                                                    onClick={() => fetchAndOpenControlModal(rule?.id, de?.id)}
                                                    className="flex items-center gap-1.5 px-2 py-1.5 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-xs font-medium border border-blue-200"
                                                    title="Mesures depuis la base de connaissances - Sélectionnez des contrôles de sécurité prédéfinis depuis notre base de données"
                                                >
                                                    <PlusCircle size={14} />
                                                    <span>Base de connaissances</span>
                                                </button>
                                                {/* Manual Control Button */}
                                                <button
                                                    onClick={() => openManualControlModal(rule?.id, de?.id)}
                                                    className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-xs font-medium border border-gray-200"
                                                    title="Ajouter un contrôle manuellement"
                                                >
                                                    <Edit3 size={14} />
                                                    <span>Ajouter manuellement</span>
                                                </button>
                                                {/* AI Assistant Button with Label */}
                                                {/* AI Assistant Button */}


                                            </div>
                                            {/* Render nested table ONLY if there are controls */}
                                            {selectedControls.length > 0 ? (
                                                <table className="w-full text-xs table-fixed">
                                                    {/* Header for nested table - REMOVED sr-only */}
                                                    <thead className="">
                                                        <tr>
                                                            <th className="text-left font-medium text-gray-500 uppercase text-xs pb-1" colSpan="2">
                                                                {t('securityControls.sections.control')}
                                                            </th>
                                                            <th className="text-right font-medium text-gray-500 uppercase text-xs pb-1 print:hidden">
                                                                {t('securityControls.sections.actions', 'Actions')}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {selectedControls.map((control) => ( // control is now an object
                                                            <React.Fragment key={`${key}-${control.id}`}>
                                                                <tr key={`${key}-${control.id}`} className="border-b border-gray-50 last:border-b-0 print:border-b-gray-300 group">
                                                                    <td className="py-4 pr-1 align-top print:py-2" colSpan="2">
                                                                        <div
                                                                            className="flex flex-col space-y-2 rounded-md px-2 py-1 transition-all duration-300"
                                                                            data-control-anchor="true"
                                                                            data-control-id={control.id != null ? String(control.id) : ''}
                                                                            data-control-name={control.name || ''}
                                                                            data-control-pair-key={key}
                                                                        >
                                                                            {/* Top Row: Measure Name and Badge */}
                                                                            <div className="flex flex-wrap items-start gap-2">
                                                                                <div className="flex items-start gap-1.5 min-w-0">
                                                                                    <span className="text-gray-400 mt-0.5 flex-shrink-0">•</span>
                                                                                    <span className={`text-xs font-medium ${control.isNewSuggestion ? 'italic text-indigo-600' : 'text-gray-700'} break-words min-w-0`}>
                                                                                        {control.name}
                                                                                    </span>
                                                                                </div>
                                                                                {control.measure_type && getMeasureTypeBadge(control.measure_type) && (
                                                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${getMeasureTypeBadge(control.measure_type).color}`}>
                                                                                        {getMeasureTypeBadge(control.measure_type).label}
                                                                                    </span>
                                                                                )}
                                                                                {isReturnTargetControl(control) && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={handleReturnToWorkshop5}
                                                                                        className="inline-flex items-center px-2.5 py-1 text-[11px] font-semibold text-indigo-700 bg-indigo-100 border border-indigo-300 rounded-md hover:bg-indigo-200 transition-colors"
                                                                                        title="Retourner à l’Atelier 5"
                                                                                    >
                                                                                        <ArrowLeft size={13} className="mr-1.5" />
                                                                                        Retour Atelier 5
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    {/* Action Area */}
                                                                    <td className="py-4 pl-1 align-top text-right print:hidden">
                                                                        <div className="flex items-center justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                            {/* Edit Button - Only for Manual or Custom controls */}
                                                                            {(control.type === 'Manual' || control.id?.toString().startsWith('manual-')) && (
                                                                                <button
                                                                                    onClick={() => handleEditControl(key, control)}
                                                                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                                                    title={t('common.edit', 'Modifier')}
                                                                                >
                                                                                    <Edit size={14} />
                                                                                </button>
                                                                            )}
                                                                            <button
                                                                                onClick={() => handleDeleteControl(key, control.id, control.name)}
                                                                                className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                                title={t('common.delete', 'Supprimer')}
                                                                            >
                                                                                <Trash2 size={14} />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                                <tr key={`${key}-${control.id}-details`} className="border-b border-gray-50 last:border-b-0 print:border-b-gray-300">
                                                                    <td colSpan="3" className="pb-4">
                                                                        {/* Bottom Row: Decision and Responsible Horizontal Layout */}
                                                                        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-1 pt-2 border-t border-gray-50">
                                                                            {/* Decision Section */}
                                                                            <div className="flex items-center gap-2 min-w-[220px]">
                                                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                                                                                    {t('securityControls.sections.decision')}:
                                                                                </span>
                                                                                <div className="flex-1">
                                                                                    <div className="flex flex-col space-y-0.5">
                                                                                        {/* Select for Screen */}
                                                                                        <select
                                                                                            value={implementationDecisions?.[key]?.[control.id]?.decision || ''}
                                                                                            onChange={(e) => handleDecisionChange(key, control.id, e.target.value, implementationDecisions?.[key]?.[control.id]?.date)}
                                                                                            className="w-full py-1 px-2 pr-6 border border-gray-300 rounded text-xs print:hidden bg-white cursor-pointer hover:border-gray-400 transition-colors"
                                                                                        >
                                                                                            <option value="" disabled>{t('securityControls.placeholders.selectDecision')}</option>
                                                                                            {getDecisionOptions(t).map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                                                                                        </select>
                                                                                        {/* Span for Print */}
                                                                                        <span className="hidden print:inline text-xs font-medium text-gray-800">
                                                                                            {getDecisionOptions(t).find(opt => opt.value === implementationDecisions?.[key]?.[control.id]?.decision)?.label || t('securityControls.severity.undefined')}
                                                                                            {implementationDecisions?.[key]?.[control.id]?.decision === 'apply_before' && implementationDecisions?.[key]?.[control.id]?.date ?
                                                                                                ` (${implementationDecisions?.[key]?.[control.id]?.date})` : ''}
                                                                                        </span>

                                                                                        {/* Date Input for Screen (Conditional) */}
                                                                                        {implementationDecisions?.[key]?.[control.id]?.decision === 'apply_before' && (
                                                                                            <input
                                                                                                type="date"
                                                                                                value={implementationDecisions?.[key]?.[control.id]?.date || ''}
                                                                                                onChange={(e) => handleDecisionChange(key, control.id, 'apply_before', e.target.value)}
                                                                                                className="w-full p-0.5 border border-gray-300 rounded text-xs mt-0.5 print:hidden"
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>

                                                                            {/* Responsible Section */}
                                                                            <div className="flex items-center gap-2 min-w-[220px]">
                                                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                                                                                    {t('securityControls.sections.responsible')}:
                                                                                </span>
                                                                                <div className="flex-1">
                                                                                    {/* Added print-only span */}
                                                                                    <span className="hidden print:inline text-xs font-medium text-gray-800">
                                                                                        {responsiblePersons?.[key]?.[control.id] || '-'}
                                                                                    </span>

                                                                                    {/* Dropdown for screen */}
                                                                                    <div className="print:hidden">
                                                                                        {(() => {
                                                                                            const currentValue = responsiblePersons?.[key]?.[control.id] || '';
                                                                                            const responsiblesList = currentContextData?.responsiblePersonsList || [];

                                                                                            return (
                                                                                                <>
                                                                                                    <select
                                                                                                        value={currentValue}
                                                                                                        onChange={(e) => handleResponsibleChange(key, control.id, e.target.value)}
                                                                                                        className="w-full py-1 px-2 pr-6 border border-gray-300 rounded text-xs bg-white cursor-pointer hover:border-gray-400 transition-colors"
                                                                                                    >
                                                                                                        <option value="">{t('context.responsiblePersons.selectResponsible')}</option>
                                                                                                        {responsiblesList.length > 0 ? (
                                                                                                            responsiblesList.map(person => (
                                                                                                                <option key={person.id} value={person.name}>{person.name}{person.role ? ` (${person.role})` : ''}</option>
                                                                                                            ))
                                                                                                        ) : (
                                                                                                            <option disabled>{t('context.responsiblePersons.noResponsibles') || 'Aucun responsable défini'}</option>
                                                                                                        )}
                                                                                                    </select>
                                                                                                    {responsiblesList.length === 0 && (
                                                                                                        <p className="text-[10px] text-amber-600 mt-0.5">
                                                                                                            {t('context.responsiblePersons.addResponsiblesHint') || 'Ajoutez des responsables dans Contexte'}
                                                                                                        </p>
                                                                                                    )}
                                                                                                </>
                                                                                            );
                                                                                        })()}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            </React.Fragment>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <div className="text-center text-xs text-gray-500 italic py-1 print:hidden print-internal-hidden"> {t('securityControls.status.noControlsSelected')} </div>
                                            )}
                                                </>
                                            )}
                                        </td>
                                        {/* Residual Severity */}
                                        <td className="border px-3 py-3 text-center align-middle print:py-1 print:px-1.5">
                                            {isNoDe ? (
                                                <span className="text-sm text-gray-400">—</span>
                                            ) : (
                                                <>
                                                    <select
                                                        value={residualSeverity || ''}
                                                        onChange={(e) => handleResidualSeverityChange(key, e.target.value)}
                                                        className={`w-full p-1 pr-8 border rounded text-xs print:hidden font-medium cursor-pointer ${residualSeverityInfo.textClass} ${residualSeverityInfo.bgClass}`}
                                                        style={{ backgroundColor: residualSeverityInfo.bgClass.startsWith('bg-') ? undefined : '#ffffff' }}
                                                    >
                                                        <option value="" disabled className="bg-white text-black">{t('securityControls.placeholders.selectSeverity')}</option>
                                                        {filteredSeverityOptions.map(opt => (
                                                            <option key={opt.value} value={opt.value} className="bg-white text-black">{opt.label}</option>
                                                        ))}
                                                    </select>
                                                    <span className={`hidden print:inline text-xs font-medium ${residualSeverityInfo.textClass} ${printResidualTextClass}`}>
                                                        {residualSeverity ? `${residualSeverityInfo.label} (${residualSeverityInfo.level})` : t('securityControls.severity.undefined')}
                                                    </span>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr className="print:hidden">
                                <td colSpan="7" className="text-center py-10 text-gray-500">
                                    {/* Updated Empty State Message */}
                                    {t('securityControls.status.noLinkedRules')}
                                    <br /> {t('securityControls.status.linkRulesFirst')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div> {/* End overflow-x-auto / print-target — Table 1 */}

            {/* ====== Table 2: Non-Intentional Risks ====== */}
            {nonIntentionalPairs && nonIntentionalPairs.length > 0 && (
            <div className="mt-8 overflow-x-auto">
                <h2 className="text-lg font-semibold text-gray-800 mb-3">{t('securityControls.table.sectionNonInt', 'Règles × Risques Non Intentionnels')}</h2>
                <table className="min-w-full border-collapse border border-gray-300">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="border px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider print:py-1 print:px-1.5 w-[18%]">{t('securityControls.table.headers.rule')}</th>
                            <th className="border px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider print:py-1 print:px-1.5 w-[15%]">{t('securityControls.table.headers.nonIntRisk', 'Risque Non Intentionnel')}</th>
                            <th className="border px-3 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider print:py-1 print:px-1.5 w-[10%]">{t('securityControls.table.headers.vraisemblanceInitiale', 'Vraisemblance Initiale')}</th>
                            <th className="border px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider print:py-1 print:px-1.5 w-[35%]" colSpan="3">{t('securityControls.table.headers.controls')}</th>
                            <th className="border px-3 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider print:py-1 print:px-1.5 w-[10%]">{t('securityControls.table.headers.vraisemblanceResiduelle', 'Vraisemblance Résiduelle')}</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {nonIntentionalPairs.map(({ rule, de }, index) => {
                            const ruleIdForKey = rule?.id || `rule-${index}`;
                            const deIdForKey = de?.id || `de-${index}`;
                            const key = `${ruleIdForKey}_${deIdForKey}`;

                            const isNewRuleGroup = index === 0 || (rule?.id && nonIntentionalPairs[index - 1]?.rule?.id !== rule.id);

                            let ruleRowSpan = 1;
                            if (isNewRuleGroup && rule?.id) {
                                for (let i = index + 1; i < nonIntentionalPairs.length; i++) {
                                    if (nonIntentionalPairs[i]?.rule?.id === rule.id) {
                                        ruleRowSpan++;
                                    } else {
                                        break;
                                    }
                                }
                            }

                            const severityInfo = getSeverityLevelInfo(de.severity, t);
                            const selectedControls = selectedControlsForRuleDE[key] || [];
                            const currentTreatmentOption = treatmentOptions[key];

                            // Vraisemblance
                            const currentVraisemblanceInit = vraisemblanceInitiale[key];
                            const currentVraisemblanceRes = vraisemblanceResiduelle[key];

                            // Determine row background based on vraisemblance reduction
                            let rowBgClass = 'bg-white hover:bg-gray-50 transition-colors duration-150';
                            if (currentVraisemblanceInit && currentVraisemblanceRes) {
                                if (currentVraisemblanceRes < currentVraisemblanceInit) rowBgClass = 'bg-green-50 hover:bg-green-100 transition-colors duration-150';
                                else if (currentVraisemblanceRes === currentVraisemblanceInit) rowBgClass = 'bg-yellow-50 hover:bg-yellow-100 transition-colors duration-150';
                            }

                            return (
                                <tr key={key} className={rowBgClass}>
                                    {/* Rule column */}
                                    {isNewRuleGroup && (
                                        <td className="border px-3 py-3 align-top print:py-1 print:px-1.5" rowSpan={ruleRowSpan}>
                                            <div className="font-medium text-sm text-gray-800 mb-1" title={rule?.description}>{rule?.name || t('securityControls.status.unknownRule')}</div>
                                            {(() => {
                                                const statusLevel = normalizeRuleStatus(rule?.status);
                                                const config = getRuleStatusConfig(statusLevel, t);
                                                return (
                                                    <div className="space-y-0.5">
                                                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-0.5">{t('securityFoundation.sections.status')}</span>
                                                        <span className={`inline-flex items-center text-xs leading-5 font-semibold rounded-full px-2 py-0.5 ${config.bgClass} ${config.textClass}`}>
                                                            {config.label}
                                                        </span>
                                                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mt-1 mb-0.5">{t('securityFoundation.sections.justification')}</span>
                                                        <div className="text-xs text-gray-400">
                                                            {rule?.justification || <span className="italic">—</span>}
                                                        </div>
                                                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mt-1 mb-0.5">{t('securityFoundation.summary.headers.gapComment')}</span>
                                                        <div className="text-xs text-gray-500 italic break-words">
                                                            {ruleGapComments[rule?.id] ? ruleGapComments[rule.id] : <span className="italic">—</span>}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                    )}

                                    {/* Non-Intentional Risk + Severity */}
                                    <td className="border px-3 py-3 align-middle print:py-1 print:px-1.5">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-sm font-medium text-gray-800">{de?.name || t('securityControls.status.unknownEvent')}</span>
                                            <span className={`inline-flex items-center self-start text-xs leading-5 font-semibold rounded-full px-2 py-0.5 ${severityInfo.bgClass} ${severityInfo.textClass}`}>
                                                {severityInfo.label} ({severityInfo.level})
                                            </span>
                                        </div>
                                    </td>

                                    {/* Vraisemblance Initiale */}
                                    <td className="border px-3 py-3 text-center align-middle print:py-1 print:px-1.5">
                                        <select
                                            value={currentVraisemblanceInit || ''}
                                            onChange={(e) => handleVraisemblanceInitialeChange(key, e.target.value)}
                                            className="w-full p-1 pr-8 border-2 border-gray-300 rounded-md text-xs font-medium focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all print:hidden bg-white cursor-pointer text-center"
                                        >
                                            <option value="" disabled className="bg-white text-black">—</option>
                                            {[1, 2, 3, 4].map(val => (
                                                <option key={val} value={val} className="bg-white text-black">{val}</option>
                                            ))}
                                        </select>
                                        <span className="hidden print:inline text-xs font-medium text-gray-800">
                                            {currentVraisemblanceInit || '—'}
                                        </span>
                                    </td>

                                    {/* Controls column (same as Table 1) */}
                                    <td className="border px-1 py-1 align-top print:py-0.5 print:px-0.5" colSpan="3">
                                        {['transfer', 'avoid', 'accept'].includes(currentTreatmentOption) ? (
                                            <div className="p-2 h-full flex flex-col">
                                                <label className="text-xs font-medium text-gray-700 mb-1">{t('securityControls.sections.justification')}</label>
                                                <textarea
                                                    className="w-full flex-grow p-2 border border-gray-300 rounded text-xs focus:ring-blue-500 focus:border-blue-500 resize-none print:hidden min-h-[80px]"
                                                    value={treatmentJustifications[key] || ''}
                                                    onChange={(e) => handleJustificationChange(key, e.target.value)}
                                                    placeholder={t('securityControls.placeholders.justificationPlaceholder', 'Justifiez votre choix de traitement...')}
                                                />
                                                <div className="hidden print:block text-xs mt-1 whitespace-pre-wrap">
                                                    {treatmentJustifications[key] || <span className="italic text-gray-400">Aucune justification</span>}
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="flex items-center gap-2 mb-2 print:hidden print-internal-hidden">
                                                    <button
                                                        onClick={() => fetchAndOpenControlModal(rule?.id, de?.id)}
                                                        className="flex items-center gap-1.5 px-2 py-1.5 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-xs font-medium border border-blue-200"
                                                        title="Mesures depuis la base de connaissances"
                                                    >
                                                        <PlusCircle size={14} />
                                                        <span>Base de connaissances</span>
                                                    </button>
                                                    <button
                                                        onClick={() => openManualControlModal(rule?.id, de?.id)}
                                                        className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-xs font-medium border border-gray-200"
                                                        title="Ajouter un contrôle manuellement"
                                                    >
                                                        <Edit3 size={14} />
                                                        <span>Ajouter manuellement</span>
                                                    </button>
                                                </div>
                                                {selectedControls.length > 0 ? (
                                                    <table className="w-full text-xs table-fixed">
                                                        <thead className="">
                                                            <tr>
                                                                <th className="text-left font-medium text-gray-500 uppercase text-xs pb-1" colSpan="2">
                                                                    {t('securityControls.sections.control')}
                                                                </th>
                                                                <th className="text-right font-medium text-gray-500 uppercase text-xs pb-1 print:hidden">
                                                                    {t('securityControls.sections.actions', 'Actions')}
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {selectedControls.map((control) => (
                                                                <React.Fragment key={`${key}-${control.id}`}>
                                                                    <tr key={`${key}-${control.id}`} className="border-b border-gray-50 last:border-b-0 print:border-b-gray-300 group">
                                                                        <td className="py-4 pr-1 align-top print:py-2" colSpan="2">
                                                                            <div
                                                                                className="flex flex-col space-y-2 rounded-md px-2 py-1 transition-all duration-300"
                                                                                data-control-anchor="true"
                                                                                data-control-id={control.id != null ? String(control.id) : ''}
                                                                                data-control-name={control.name || ''}
                                                                                data-control-pair-key={key}
                                                                            >
                                                                                <div className="flex flex-wrap items-start gap-2">
                                                                                    <div className="flex items-start gap-1.5 min-w-0">
                                                                                        <span className="text-gray-400 mt-0.5 flex-shrink-0">•</span>
                                                                                        <span className={`text-xs font-medium ${control.isNewSuggestion ? 'italic text-indigo-600' : 'text-gray-700'} break-words min-w-0`}>
                                                                                            {control.name}
                                                                                        </span>
                                                                                    </div>
                                                                                    {control.measure_type && getMeasureTypeBadge(control.measure_type) && (
                                                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${getMeasureTypeBadge(control.measure_type).color}`}>
                                                                                            {getMeasureTypeBadge(control.measure_type).label}
                                                                                        </span>
                                                                                    )}
                                                                                    {isReturnTargetControl(control) && (
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={handleReturnToWorkshop5}
                                                                                            className="inline-flex items-center px-2.5 py-1 text-[11px] font-semibold text-indigo-700 bg-indigo-100 border border-indigo-300 rounded-md hover:bg-indigo-200 transition-colors"
                                                                                            title="Retourner à l’Atelier 5"
                                                                                        >
                                                                                            <ArrowLeft size={13} className="mr-1.5" />
                                                                                            Retour Atelier 5
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td className="py-4 pl-1 align-top text-right print:hidden">
                                                                            <div className="flex items-center justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                                {(control.type === 'Manual' || control.id?.toString().startsWith('manual-')) && (
                                                                                    <button
                                                                                        onClick={() => handleEditControl(key, control)}
                                                                                        className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                                                        title={t('common.edit', 'Modifier')}
                                                                                    >
                                                                                        <Edit size={14} />
                                                                                    </button>
                                                                                )}
                                                                                <button
                                                                                    onClick={() => handleDeleteControl(key, control.id, control.name)}
                                                                                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                                                    title={t('common.delete', 'Supprimer')}
                                                                                >
                                                                                    <Trash2 size={14} />
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                    <tr key={`${key}-${control.id}-details`} className="border-b border-gray-50 last:border-b-0 print:border-b-gray-300">
                                                                        <td colSpan="3" className="pb-4">
                                                                            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-1 pt-2 border-t border-gray-50">
                                                                                <div className="flex items-center gap-2 min-w-[220px]">
                                                                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                                                                                        {t('securityControls.sections.decision')}:
                                                                                    </span>
                                                                                    <div className="flex-1">
                                                                                        <div className="flex flex-col space-y-0.5">
                                                                                            <select
                                                                                                value={implementationDecisions?.[key]?.[control.id]?.decision || ''}
                                                                                                onChange={(e) => handleDecisionChange(key, control.id, e.target.value, implementationDecisions?.[key]?.[control.id]?.date)}
                                                                                                className="w-full py-1 px-2 pr-6 border border-gray-300 rounded text-xs print:hidden bg-white cursor-pointer hover:border-gray-400 transition-colors"
                                                                                            >
                                                                                                <option value="" disabled>{t('securityControls.placeholders.selectDecision')}</option>
                                                                                                {getDecisionOptions(t).map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                                                                                            </select>
                                                                                            <span className="hidden print:inline text-xs font-medium text-gray-800">
                                                                                                {getDecisionOptions(t).find(opt => opt.value === implementationDecisions?.[key]?.[control.id]?.decision)?.label || t('securityControls.severity.undefined')}
                                                                                                {implementationDecisions?.[key]?.[control.id]?.decision === 'apply_before' && implementationDecisions?.[key]?.[control.id]?.date ?
                                                                                                    ` (${implementationDecisions?.[key]?.[control.id]?.date})` : ''}
                                                                                            </span>
                                                                                            {implementationDecisions?.[key]?.[control.id]?.decision === 'apply_before' && (
                                                                                                <input
                                                                                                    type="date"
                                                                                                    value={implementationDecisions?.[key]?.[control.id]?.date || ''}
                                                                                                    onChange={(e) => handleDecisionChange(key, control.id, 'apply_before', e.target.value)}
                                                                                                    className="w-full p-0.5 border border-gray-300 rounded text-xs mt-0.5 print:hidden"
                                                                                                />
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex items-center gap-2 min-w-[220px]">
                                                                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                                                                                        {t('securityControls.sections.responsible')}:
                                                                                    </span>
                                                                                    <div className="flex-1">
                                                                                        <span className="hidden print:inline text-xs font-medium text-gray-800">
                                                                                            {responsiblePersons?.[key]?.[control.id] || '-'}
                                                                                        </span>
                                                                                        <div className="print:hidden">
                                                                                            {(() => {
                                                                                                const currentValue = responsiblePersons?.[key]?.[control.id] || '';
                                                                                                const responsiblesList = currentContextData?.responsiblePersonsList || [];
                                                                                                return (
                                                                                                    <>
                                                                                                        <select
                                                                                                            value={currentValue}
                                                                                                            onChange={(e) => handleResponsibleChange(key, control.id, e.target.value)}
                                                                                                            className="w-full py-1 px-2 pr-6 border border-gray-300 rounded text-xs bg-white cursor-pointer hover:border-gray-400 transition-colors"
                                                                                                        >
                                                                                                            <option value="">{t('context.responsiblePersons.selectResponsible')}</option>
                                                                                                            {responsiblesList.length > 0 ? (
                                                                                                                responsiblesList.map(person => (
                                                                                                                    <option key={person.id} value={person.name}>{person.name}{person.role ? ` (${person.role})` : ''}</option>
                                                                                                                ))
                                                                                                            ) : (
                                                                                                                <option disabled>{t('context.responsiblePersons.noResponsibles') || 'Aucun responsable défini'}</option>
                                                                                                            )}
                                                                                                        </select>
                                                                                                        {responsiblesList.length === 0 && (
                                                                                                            <p className="text-[10px] text-amber-600 mt-0.5">
                                                                                                                {t('context.responsiblePersons.addResponsiblesHint') || 'Ajoutez des responsables dans Contexte'}
                                                                                                            </p>
                                                                                                        )}
                                                                                                    </>
                                                                                                );
                                                                                            })()}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                </React.Fragment>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div className="text-center text-xs text-gray-500 italic py-1 print:hidden print-internal-hidden"> {t('securityControls.status.noControlsSelected')} </div>
                                                )}
                                            </>
                                        )}
                                    </td>

                                    {/* Vraisemblance Résiduelle */}
                                    <td className="border px-3 py-3 text-center align-middle print:py-1 print:px-1.5">
                                        <select
                                            value={currentVraisemblanceRes || ''}
                                            onChange={(e) => handleVraisemblanceResiduelleChange(key, e.target.value)}
                                            className="w-full p-1 pr-8 border-2 border-gray-300 rounded-md text-xs font-medium focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all print:hidden bg-white cursor-pointer text-center"
                                        >
                                            <option value="" disabled className="bg-white text-black">—</option>
                                            {(currentVraisemblanceInit ? Array.from({length: currentVraisemblanceInit}, (_, i) => i + 1) : [1, 2, 3, 4]).map(val => (
                                                <option key={val} value={val} className="bg-white text-black">{val}</option>
                                            ))}
                                        </select>
                                        <span className="hidden print:inline text-xs font-medium text-gray-800">
                                            {currentVraisemblanceRes || '—'}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                        {nonIntentionalPairs.length === 0 && (
                            <tr className="print:hidden">
                                <td colSpan="6" className="text-center py-10 text-gray-500">
                                    {t('securityControls.status.noLinkedRules')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            )}

            {/* Modals - Hidden on Print */}
            <div className="print:hidden">
                {/* Updated ControlSelectionModal props */}
                <ControlSelectionModal
                    isOpen={isControlModalOpen}
                    onClose={() => setIsControlModalOpen(false)}
                    modalAvailableControls={modalAvailableControls} // Pass fetched controls
                    isLoading={isModalLoading} // Pass loading state
                    error={modalError} // Pass error state
                    initialSelectedIds={(() => {
                        if (!modalContext) return [];
                        const deIdPart = modalContext.deId != null ? modalContext.deId : '__no_de__';
                        const modalKey = `${modalContext.ruleId}_${deIdPart}`;
                        const existing = selectedControlsForRuleDE[modalKey];
                        return existing
                            ? existing.filter(c => !c.isNewSuggestion && c.type !== 'AI').map(c => c.id)
                            : [];
                    })()}
                    onSave={handleSaveControlSelection}
                />
                <GlobalAiSuggestionModal
                    isOpen={isGlobalAiModalOpen}
                    onClose={() => setIsGlobalAiModalOpen(false)}
                    suggestions={globalAiSuggestions}
                    isLoading={isGlobalAiLoading}
                    error={globalAiError}
                    onAcceptSuggestions={handleAcceptGlobalSuggestions}
                    onRegenerate={() => handleGlobalAiGeneration(true)}
                    ruleNamesMap={Object.fromEntries(
                        linkedPairs.map(({ rule }) => [rule.id, rule.name])
                    )} // Pass rule names for display
                />
                <ManualControlModal
                    isOpen={isManualModalOpen}
                    onClose={() => setIsManualModalOpen(false)}
                    onSave={handleSaveManualControl}
                    initialData={manualModalContext?.control}
                />
                <DeleteConfirmationModal
                    isOpen={deleteConfirmation.isOpen}
                    onClose={() => setDeleteConfirmation({ ...deleteConfirmation, isOpen: false })}
                    onConfirm={confirmDeleteControl}
                    itemName={deleteConfirmation.itemName}
                    itemType="securityControl"
                    title={t('securityControls.modal.deleteTitle', 'Supprimer le contrôle')}
                    message={t('securityControls.modal.deleteMessage', 'Êtes-vous sûr de vouloir supprimer ce contrôle de sécurité ?')}
                />
            </div>

        </div > // End main container
    );

}; // End SecurityControls Component

export default SecurityControls;
